// ─── FerMat — SVG Concept Visual Library v3 ─────────────────────
// Visual concepts are rendered with depth: gradients for surfaces, layered
// drop-shadows for grounding, and subtle highlights to mimic real-world
// materials. Shared primitives (gradients, shadows) live in ./core.jsx.
import { useId } from 'react';
import {
  vColors, ShadowDef, LinearLight, SphereGradient, RichShadow, darken, lighten,
} from './core.jsx';
export { vColors, ShadowDef };

// ─── 1. NumberLineVisual ───────────────────────────────────────────────────────
// Polished: axis with subtle gradient + grounding shadow, tick hierarchy
// (major every integer, minor at half-steps for short ranges), highlighted
// numbers shown as glossy spheres with their own labelled badge underneath.
export function NumberLineVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const { start = 0, end = 5, highlight = [], natural = false } = params;
  const pad = 22, lineY = size * 0.55;
  const range = end - start || 1;
  const toX = n => pad + ((n - start) / range) * (size - pad * 2);
  const ticks = [];
  for (let i = start; i <= end; i++) ticks.push(i);
  // Half-step minor ticks when range is small enough to feel readable
  const showMinor = range <= 10;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="jimarxêz">
      <defs>
        <LinearLight id={`${uid}_axis`} from={c.muted} to={c.line} x1="0%" y1="0%" x2="100%" y2="0%" />
        <SphereGradient id={`${uid}_sphere`} base={c.fill1} />
        <RichShadow id={`${uid}_rich`} near={1.2} far={3} />
        <ShadowDef id={`${uid}_sh`} blur={1.5} opacity={0.20} />
        <marker id={`${uid}_arr`} markerWidth="9" markerHeight="9" refX="4" refY="4" orient="auto">
          <path d="M0,1 L7,4 L0,7 Z" fill={c.muted} />
        </marker>
      </defs>

      {/* Axis baseline shadow (grounds the line) */}
      <line x1={pad - 6} y1={lineY + 2.5} x2={size - pad + 6} y2={lineY + 2.5}
        stroke="#000" strokeWidth="0.8" opacity="0.08" />
      {/* Axis line — subtle gradient stroke */}
      <line x1={pad - 6} y1={lineY} x2={size - pad + 6} y2={lineY}
        stroke={`url(#${uid}_axis)`} strokeWidth="2.2" strokeLinecap="round"
        markerEnd={`url(#${uid}_arr)`} />

      {natural && (
        <text x={size - pad + 10} y={lineY + 4} fontSize={11} fill={c.muted} fontStyle="italic">…</text>
      )}

      {/* Minor (half) ticks underneath, only when readable */}
      {showMinor && ticks.slice(0, -1).map(n => {
        const x = (toX(n) + toX(n + 1)) / 2;
        return (
          <line key={`m${n}`} x1={x} y1={lineY - 3} x2={x} y2={lineY + 3}
            stroke={c.muted} strokeWidth="0.8" opacity="0.55" />
        );
      })}

      {/* Major ticks + labels / spheres */}
      {ticks.map(n => {
        const x = toX(n);
        const isHi = highlight.includes(n);
        return (
          <g key={n}>
            {/* Tick mark — taller for highlighted */}
            <line
              x1={x} y1={lineY - (isHi ? 8 : 5)}
              x2={x} y2={lineY + (isHi ? 8 : 5)}
              stroke={isHi ? c.fill1 : c.muted}
              strokeWidth={isHi ? 2.2 : 1.3}
              strokeLinecap="round"
            />
            {isHi ? (
              <g filter={`url(#${uid}_rich)`}>
                {/* Sphere body */}
                <circle cx={x} cy={lineY - 20} r={13} fill={c.fill1} />
                {/* Glossy gradient overlay */}
                <circle cx={x} cy={lineY - 20} r={13} fill={`url(#${uid}_sphere)`} />
                {/* Soft top-left specular highlight */}
                <ellipse cx={x - 3.5} cy={lineY - 24} rx="4" ry="2.2"
                  fill="#fff" opacity="0.45" />
                {/* Number label centred */}
                <text x={x} y={lineY - 16} textAnchor="middle" fontSize={11}
                  fontWeight="800" fill={c.white} dominantBaseline="middle"
                  style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.25))' }}>
                  {n}
                </text>
              </g>
            ) : (
              <text x={x} y={lineY + 21} textAnchor="middle" fontSize={11}
                fontWeight="600" fill={c.dim}>
                {n}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ─── 2. CountingVisual ────────────────────────────────────────────────────────
// Polished: every counter is a glossy 3D object with a radial-gradient body
// and a specular highlight at the top-left, grounded by a soft shadow. Groups
// alternate between primary and a secondary brand tint without losing depth.
export function CountingVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const { count = 5, shape = 'circle', color = 'primary', groups = 1 } = params;
  const n = Math.min(count, 20);
  const cols = n <= 5 ? n : Math.ceil(Math.sqrt(n));
  const rows = Math.ceil(n / cols);
  const r = Math.min((size * 0.72) / (cols * 2.6), (size * 0.72) / (rows * 2.6), 18);
  const gapX = Math.min((size * 0.9 - r * 2) / Math.max(cols - 1, 1), r * 2.4);
  const gapY = Math.min((size * 0.72 - r * 2) / Math.max(rows - 1, 1), r * 2.6);
  const startX = (size - (cols - 1) * gapX) / 2;
  const startY = size * 0.12 + (size * 0.68 - (rows - 1) * gapY) / 2;
  const primaryFill   = color === 'accent' ? c.fill2 : color === 'success' ? c.fill3 : c.fill1;
  const secondaryFill = primaryFill === c.fill1 ? c.fill2 : c.fill1;

  const items = [];
  for (let i = 0; i < n; i++) {
    const row = Math.floor(i / cols), col = i % cols;
    const cx = startX + col * gapX, cy = startY + row * gapY;
    const grpColor = groups > 1
      ? (Math.floor(i / Math.ceil(n / groups)) % 2 === 0 ? primaryFill : secondaryFill)
      : primaryFill;
    const gradId = `${uid}_g${i}`;
    const key = `item${i}`;

    // Common props: depth shadow + body fill + glossy overlay
    if (shape === 'star') {
      const pts = Array.from({length:10}, (_,j) => {
        const a = (j * Math.PI) / 5 - Math.PI / 2;
        const rad = j % 2 === 0 ? r : r * 0.45;
        return `${cx + rad * Math.cos(a)},${cy + rad * Math.sin(a)}`;
      }).join(' ');
      items.push(
        <g key={key} filter={`url(#${uid}_rich)`}>
          <polygon points={pts} fill={grpColor} />
          <polygon points={pts} fill={`url(#${gradId})`} />
        </g>
      );
    } else if (shape === 'triangle') {
      items.push(
        <g key={key} filter={`url(#${uid}_rich)`}>
          <polygon points={`${cx},${cy-r} ${cx-r},${cy+r} ${cx+r},${cy+r}`} fill={grpColor} />
          <polygon points={`${cx},${cy-r} ${cx-r},${cy+r} ${cx+r},${cy+r}`} fill={`url(#${gradId})`} />
        </g>
      );
    } else if (shape === 'square') {
      items.push(
        <g key={key} filter={`url(#${uid}_rich)`}>
          <rect x={cx-r} y={cy-r} width={r*2} height={r*2} rx={r * 0.18} fill={grpColor} />
          <rect x={cx-r} y={cy-r} width={r*2} height={r*2} rx={r * 0.18} fill={`url(#${gradId})`} />
          {/* Top-left glossy stripe */}
          <rect x={cx-r+2} y={cy-r+2} width={r*2-4} height={r*0.5} rx={r*0.14}
            fill="#fff" opacity="0.22" />
        </g>
      );
    } else {
      // Circle — the canonical "counter token"
      items.push(
        <g key={key} filter={`url(#${uid}_rich)`}>
          <circle cx={cx} cy={cy} r={r} fill={grpColor} />
          <circle cx={cx} cy={cy} r={r} fill={`url(#${gradId})`} />
          {/* Specular highlight */}
          <ellipse cx={cx - r * 0.32} cy={cy - r * 0.40} rx={r * 0.36} ry={r * 0.22}
            fill="#fff" opacity="0.50" />
        </g>
      );
    }
  }

  // Collect unique gradient defs (one per item so each has a centred highlight)
  const gradientDefs = [];
  for (let i = 0; i < n; i++) {
    const row = Math.floor(i / cols), col = i % cols;
    const grpColor = groups > 1
      ? (Math.floor(i / Math.ceil(n / groups)) % 2 === 0 ? primaryFill : secondaryFill)
      : primaryFill;
    gradientDefs.push(
      <SphereGradient key={`g${i}`} id={`${uid}_g${i}`} base={grpColor} cx="32%" cy="28%" />
    );
  }

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${n} tişt`}>
      <defs>
        <RichShadow id={`${uid}_rich`} near={0.8} far={2.2} opacityNear={0.20} opacityFar={0.10} />
        {gradientDefs}
      </defs>
      {items}
      {/* Total count badge */}
      <g transform={`translate(${size/2}, ${size * 0.92})`}>
        <rect x={-16} y={-10} width={32} height={20} rx={10}
          fill={primaryFill} opacity="0.10" />
        <text x={0} y={1} textAnchor="middle" dominantBaseline="middle"
          fontSize={13} fontWeight="800" fill={primaryFill}>
          {n}
        </text>
      </g>
    </svg>
  );
}

// ─── 3. BlocksVisual ─────────────────────────────────────────────────────────
// Polished: base-10 blocks rendered with isometric 3D depth. Each cell is a
// little cube — top face is lightest, left face medium, right face darker —
// so the place-value relationship reads as physical building blocks.
//   hundred = a 5×5 layered plate (one stack of cubes, viewed isometrically)
//   ten     = a 1×5 vertical stick
//   one     = a single cube
export function BlocksVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const { ones = 3, tens = 2, hundreds = 1 } = params;
  const hC = Math.min(hundreds, 2), tC = Math.min(tens, 5), oC = Math.min(ones, 9);

  // ── Helper: render an isometric cube at (x, y) with edge length `s` ──
  // Light from top-left: top face lightest, left medium, right darkest.
  const cube = (key, x, y, s, color) => {
    const dx = s * 0.55;   // isometric horizontal offset
    const dy = s * 0.30;   // isometric vertical offset
    const top    = lighten(color, 0.28);
    const left   = color;
    const right  = darken(color, 0.20);
    // Top: rhombus
    const topPts = [
      [x, y],
      [x + dx, y - dy],
      [x + s + dx, y - dy],
      [x + s, y],
    ].map(p => p.join(',')).join(' ');
    // Left face
    const leftPts = [
      [x, y],
      [x, y + s],
      [x + s, y + s],
      [x + s, y],
    ].map(p => p.join(',')).join(' ');
    // Right face
    const rightPts = [
      [x + s, y],
      [x + s + dx, y - dy],
      [x + s + dx, y + s - dy],
      [x + s, y + s],
    ].map(p => p.join(',')).join(' ');
    return (
      <g key={key}>
        <polygon points={leftPts}  fill={left}  stroke={darken(color, 0.32)} strokeWidth={0.4} strokeLinejoin="round" />
        <polygon points={rightPts} fill={right} stroke={darken(color, 0.32)} strokeWidth={0.4} strokeLinejoin="round" />
        <polygon points={topPts}   fill={top}   stroke={darken(color, 0.18)} strokeWidth={0.4} strokeLinejoin="round" />
      </g>
    );
  };

  const elems = [];
  const pad = size * 0.10;
  const labelH = size * 0.14;
  // Estimate widths so we can centre horizontally
  const cellSize = (hC > 0 ? (size * 0.06) : (size * 0.07));
  const cellGap  = cellSize * 0.05;
  const hundredW = (cellSize + cellGap) * 5 + cellSize * 0.55;
  const tenW     = cellSize + cellSize * 0.55;
  const oneW     = cellSize + cellSize * 0.55;
  const oneGap   = cellSize * 0.25;
  const groupGap = cellSize * 0.6;

  let cursorX = pad;
  // Compute total width for centering
  let totalW = 0;
  if (hC > 0) totalW += hC * hundredW + (hC - 1) * groupGap;
  if (tC > 0) totalW += (totalW ? groupGap : 0) + tC * tenW + (tC - 1) * cellGap * 2;
  if (oC > 0) totalW += (totalW ? groupGap : 0) + Math.min(oC, 3) * (oneW + oneGap);

  cursorX = (size - totalW) / 2;
  // Bottom-aligned y baseline for "ground" feel
  const baseY = (size - labelH - pad) - cellSize * 0.25;

  // ── Hundreds plates: 5×5 cube grid ──
  for (let h = 0; h < hC; h++) {
    for (let row = 0; row < 5; row++) {
      for (let col = 0; col < 5; col++) {
        // back rows render first so iso depth stacks correctly
        const x = cursorX + col * (cellSize + cellGap);
        const y = baseY - row * (cellSize - cellGap * 0.5);
        elems.push(cube(`h${h}r${row}c${col}`, x, y, cellSize, c.fill1));
      }
    }
    cursorX += hundredW + groupGap;
  }

  // ── Tens sticks: 5-stack ──
  for (let t = 0; t < tC; t++) {
    for (let seg = 0; seg < 5; seg++) {
      const x = cursorX;
      const y = baseY - seg * (cellSize - cellGap * 0.5);
      elems.push(cube(`t${t}s${seg}`, x, y, cellSize, c.fill2));
    }
    cursorX += tenW + cellGap;
  }
  if (tC > 0) cursorX += groupGap - cellGap;

  // ── Ones: single cubes (wrap after 3) ──
  for (let o = 0; o < oC; o++) {
    const col = o % 3, row = Math.floor(o / 3);
    const x = cursorX + col * (oneW + oneGap);
    const y = baseY - row * (cellSize + oneGap);
    elems.push(cube(`o${o}`, x, y, cellSize, c.fill3));
  }

  // ── Ground shadow plate ──
  const groundY = baseY + cellSize + 4;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="bloklar">
      <ellipse cx={size / 2} cy={groundY} rx={totalW * 0.5} ry={cellSize * 0.18}
        fill="#000" opacity="0.10" />
      {elems}
      {/* Place-value label */}
      <g transform={`translate(${size/2}, ${size - pad * 0.55})`}>
        <rect x={-totalW * 0.5} y={-9} width={Math.max(totalW, 80)} height={18} rx={9}
          fill={c.fill1} opacity="0.08" />
        <text x={0} y={1} textAnchor="middle" dominantBaseline="middle"
          fontSize={Math.min(10, size * 0.08)} fontWeight={700} fill={c.fill1}>
          {hundreds > 0 && `${hundreds}×100`}{hundreds > 0 && (tens > 0 || ones > 0) ? ' · ' : ''}
          {tens > 0 && `${tens}×10`}{tens > 0 && ones > 0 ? ' · ' : ''}
          {ones > 0 && `${ones}×1`}
        </text>
      </g>
    </svg>
  );
}

// ─── 4. FractionVisual ───────────────────────────────────────────────────────
// Polished: pie slices wear a radial gradient (centre lighter, edge darker)
// so the disc reads as a real surface; empty slices are softly tinted with a
// dashed inner ring so they're clearly "missing". Bar mode gets a hatched
// fill for the empty cells. The fraction label sits in a pill badge below.
export function FractionVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const { num = 1, den = 4, visual: vtype = 'pie' } = params;
  const cx = size / 2, cy = size * 0.46, r = size * 0.34;

  // ─── BAR ────────────────────────────────────────────────────────────────
  if (vtype === 'bar') {
    const barH = size * 0.28, barY = size * 0.31;
    const segW = (size - 36) / den;
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${num}/${den}`}>
        <defs>
          <LinearLight id={`${uid}_segG`} from={lighten(c.fill2, 0.18)} to={c.fill2} x1="0%" y1="0%" x2="0%" y2="100%" />
          <RichShadow id={`${uid}_rich`} near={1} far={2.5} />
          <pattern id={`${uid}_hatch`} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="6" height="6" fill={c.soft2} />
            <line x1="0" y1="0" x2="0" y2="6" stroke={c.fill2} strokeWidth="0.8" opacity="0.30" />
          </pattern>
        </defs>
        {Array.from({length: den}, (_, i) => {
          const filled = i < num;
          return (
            <g key={i} filter={filled ? `url(#${uid}_rich)` : undefined}>
              <rect
                x={18 + i * segW} y={barY} width={segW - 3} height={barH}
                fill={filled ? c.fill2 : `url(#${uid}_hatch)`}
                rx={4}
                stroke={filled ? darken(c.fill2, 0.10) : c.fill2}
                strokeOpacity={filled ? 1 : 0.35}
                strokeWidth={1.5}
              />
              {filled && (
                <rect
                  x={18 + i * segW} y={barY} width={segW - 3} height={barH}
                  fill={`url(#${uid}_segG)`} rx={4} opacity="0.55"
                />
              )}
            </g>
          );
        })}
        {/* Label pill */}
        <g transform={`translate(${size/2}, ${barY + barH + 22})`}>
          <rect x={-22} y={-12} width={44} height={24} rx={12} fill={c.fill2} opacity="0.12" />
          <text x={0} y={1} textAnchor="middle" dominantBaseline="middle"
            fontSize={14} fontWeight={800} fill={c.fill2}>{num}/{den}</text>
        </g>
      </svg>
    );
  }

  // ─── PIE ────────────────────────────────────────────────────────────────
  const sliceAngle = (2 * Math.PI) / den;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${num}/${den}`}>
      <defs>
        <radialGradient id={`${uid}_filled`} cx="40%" cy="36%" r="65%">
          <stop offset="0%"   stopColor={lighten(c.fill2, 0.30)} />
          <stop offset="55%"  stopColor={c.fill2} />
          <stop offset="100%" stopColor={darken(c.fill2, 0.12)} />
        </radialGradient>
        <radialGradient id={`${uid}_empty`} cx="40%" cy="36%" r="65%">
          <stop offset="0%"   stopColor={lighten(c.soft1, 0.10)} />
          <stop offset="100%" stopColor={c.soft1} />
        </radialGradient>
        <RichShadow id={`${uid}_rich`} near={1.5} far={4} />
      </defs>

      {/* Ground shadow under the pie */}
      <ellipse cx={cx} cy={cy + r + 4} rx={r * 0.85} ry={r * 0.10}
        fill="#000" opacity="0.10" />

      <g filter={`url(#${uid}_rich)`}>
        {Array.from({length: den}, (_, i) => {
          const a1 = -Math.PI / 2 + i * sliceAngle;
          const a2 = a1 + sliceAngle;
          const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
          const x2 = cx + r * Math.cos(a2), y2 = cy + r * Math.sin(a2);
          const large = sliceAngle > Math.PI ? 1 : 0;
          const filled = i < num;
          return (
            <path key={i}
              d={`M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large},1 ${x2},${y2} Z`}
              fill={filled ? `url(#${uid}_filled)` : `url(#${uid}_empty)`}
              stroke={filled ? darken(c.fill2, 0.18) : c.muted}
              strokeOpacity={filled ? 0.30 : 0.40}
              strokeWidth={filled ? 0.8 : 1}
              strokeDasharray={filled ? undefined : '3,2'}
            />
          );
        })}
      </g>

      {/* Glossy specular arc on top-left of the disc */}
      <path
        d={`M ${cx - r * 0.55},${cy - r * 0.65} A ${r * 0.85},${r * 0.85} 0 0,1 ${cx + r * 0.20},${cy - r * 0.85}`}
        stroke="#fff" strokeWidth={r * 0.18} strokeLinecap="round"
        fill="none" opacity="0.35"
      />

      {/* White separators between slices — only over the filled→empty boundary */}
      {Array.from({length: den}, (_, i) => {
        const a = -Math.PI / 2 + i * sliceAngle;
        const x = cx + r * Math.cos(a), y = cy + r * Math.sin(a);
        return (
          <line key={`sep${i}`} x1={cx} y1={cy} x2={x} y2={y}
            stroke="#fff" strokeWidth={1.6} opacity="0.85" />
        );
      })}

      {/* Centre pin */}
      <circle cx={cx} cy={cy} r={3.5} fill={c.white} stroke={c.muted} strokeOpacity="0.3" strokeWidth={0.5} />

      {/* Label pill */}
      <g transform={`translate(${cx}, ${cy + r + 22})`}>
        <rect x={-24} y={-12} width={48} height={24} rx={12} fill={c.fill2} opacity="0.12" />
        <text x={0} y={1} textAnchor="middle" dominantBaseline="middle"
          fontSize={15} fontWeight={800} fill={c.fill2}>{num}/{den}</text>
      </g>
    </svg>
  );
}

// Glossy counter shared by the operation and comparison visuals.
function Sphere({ x, y, r, base, gradientId, filterId, opacity = 1 }) {
  return (
    <g filter={`url(#${filterId})`} opacity={opacity}>
      <circle cx={x} cy={y} r={r} fill={base} />
      <circle cx={x} cy={y} r={r} fill={`url(#${gradientId})`} />
      <ellipse cx={x - r * 0.32} cy={y - r * 0.40} rx={r * 0.30} ry={r * 0.18}
        fill="#fff" opacity="0.55" />
    </g>
  );
}

// Equation caption under a visual: tinted pill sized to its text.
function EquationPill({ x, y, text, color, unit }) {
  const fontSize = 14 * unit;
  const width = text.length * fontSize * 0.60 + 26 * unit;
  const height = 26 * unit;
  return (
    <g transform={`translate(${x}, ${y})`}>
      <rect x={-width / 2} y={-height / 2} width={width} height={height} rx={height / 2}
        fill={color} opacity="0.12" />
      <text x={0} y={1} textAnchor="middle" dominantBaseline="middle"
        fontSize={fontSize} fontWeight={800} fill={color}>{text}</text>
    </g>
  );
}

// ─── 5. OperationVisual ──────────────────────────────────────────────────────
// Each operation gets the model children actually use for it, laid out on a
// unit grid and scaled to fit, with the equation as a caption underneath:
//   add  — two groups of counters joined by a "+"
//   sub  — one row, the subtracted counters crossed out
//   mul  — an a × b array (a rows of b)
//   div  — a counters shared into b boxes; any remainder sits outside
export function OperationVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const { op = 'add' } = params;
  const a = Math.max(0, params.a ?? 3), b = Math.max(0, params.b ?? 2);
  const unit = size / 160;
  const symText = { add: '+', sub: '−', mul: '×', div: '÷' }[op] || '+';
  // fill4 (amber) is too pale for text, so division uses a darker amber.
  const symColor = op === 'add' ? c.fill3 : op === 'sub' ? c.fill5 : op === 'mul' ? c.fill2 : (theme?.warningText || '#A16207');
  const result = op === 'add' ? a + b : op === 'sub' ? a - b : op === 'mul' ? a * b : b !== 0 ? Math.floor(a / b) : 0;

  // Layout in grid cells (1 cell = one counter); `marks` are the extras.
  const counters = [];   // { gx, gy, tone: 1|2, crossed }
  const boxes = [];      // { gx, gy, gw, gh } — division groups
  let symbolAt = null;   // { gx, gy }
  let gridW = 1, gridH = 1;

  if (op === 'sub') {
    const n = Math.min(a, 10), removed = Math.min(b, n);
    for (let i = 0; i < n; i++) counters.push({ gx: i, gy: 0, tone: 1, crossed: i >= n - removed });
    gridW = Math.max(n, 1);
  } else if (op === 'mul') {
    const rows = Math.min(a, 6), cols = Math.min(b, 8);
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) counters.push({ gx: col, gy: row, tone: row % 2 ? 2 : 1 });
    }
    gridW = Math.max(cols, 1); gridH = Math.max(rows, 1);
  } else if (op === 'div' && b > 0) {
    const groups = Math.min(b, 6), per = Math.min(result, 6);
    const remainder = Math.min(a - result * b, 4);
    const pitch = 1.45; // box width + gap between boxes
    for (let g = 0; g < groups; g++) {
      boxes.push({ gx: g * pitch, gy: 0, gw: 1, gh: Math.max(per, 1) });
      for (let k = 0; k < per; k++) counters.push({ gx: g * pitch, gy: k, tone: 1 });
    }
    for (let k = 0; k < remainder; k++) counters.push({ gx: groups * pitch + 0.15, gy: k, tone: 2 });
    gridW = (groups - 1) * pitch + 1 + (remainder > 0 ? pitch + 0.15 : 0);
    gridH = Math.max(per, remainder, 1);
  } else {
    const left = Math.min(a, 9), right = Math.min(b, 9);
    const gap = 1.3; // room for the "+" between the two groups
    for (let i = 0; i < left; i++) counters.push({ gx: i, gy: 0, tone: 1 });
    symbolAt = { gx: left + (gap - 1) / 2, gy: 0 };
    for (let i = 0; i < right; i++) counters.push({ gx: left + gap + i, gy: 0, tone: 2 });
    gridW = left + gap + right;
  }

  const areaX = size * 0.07, areaW = size * 0.86;
  const areaY = size * 0.08, areaH = size * 0.58;
  const cell = Math.min(areaW / gridW, areaH / gridH, 30 * unit);
  const originX = areaX + (areaW - gridW * cell) / 2;
  const originY = areaY + (areaH - gridH * cell) / 2;
  const px = gx => originX + (gx + 0.5) * cell;
  const py = gy => originY + (gy + 0.5) * cell;
  const r = cell * 0.40;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
      aria-label={`${a} ${symText} ${b} = ${result}`}>
      <defs>
        <RichShadow id={`${uid}_rich`} near={0.8} far={2.2} opacityNear={0.20} opacityFar={0.10} />
        <SphereGradient id={`${uid}_g1`} base={c.fill1} cx="32%" cy="28%" />
        <SphereGradient id={`${uid}_g2`} base={c.fill2} cx="32%" cy="28%" />
      </defs>

      {boxes.map((box, i) => (
        <rect key={`box${i}`}
          x={px(box.gx) - cell * 0.58} y={originY - cell * 0.10}
          width={cell * 1.16} height={box.gh * cell + cell * 0.20} rx={cell * 0.28}
          fill={c.fill1} fillOpacity="0.08" stroke={c.fill1} strokeOpacity="0.45"
          strokeWidth={1.4 * unit} strokeDasharray={`${4 * unit},${3 * unit}`} />
      ))}

      {counters.map((item, i) => (
        <g key={`c${i}`}>
          <Sphere x={px(item.gx)} y={py(item.gy)} r={r}
            base={item.tone === 2 ? c.fill2 : c.fill1}
            gradientId={`${uid}_g${item.tone}`} filterId={`${uid}_rich`}
            opacity={item.crossed ? 0.35 : 1} />
          {item.crossed && (
            <g stroke={c.fill5} strokeWidth={Math.max(2, r * 0.22)} strokeLinecap="round">
              <line x1={px(item.gx) - r * 0.8} y1={py(item.gy) - r * 0.8} x2={px(item.gx) + r * 0.8} y2={py(item.gy) + r * 0.8} />
              <line x1={px(item.gx) + r * 0.8} y1={py(item.gy) - r * 0.8} x2={px(item.gx) - r * 0.8} y2={py(item.gy) + r * 0.8} />
            </g>
          )}
        </g>
      ))}

      {symbolAt && (
        <text x={px(symbolAt.gx)} y={py(symbolAt.gy) + 1} textAnchor="middle" dominantBaseline="middle"
          fontSize={cell * 1.05} fontWeight={900} fill={symColor}>{symText}</text>
      )}

      <EquationPill x={size / 2} y={size * 0.83} unit={unit} color={symColor}
        text={`${a} ${symText} ${b} = ${result}`} />
    </svg>
  );
}

// ─── 6. CompareVisual ────────────────────────────────────────────────────────
// Two left-aligned rows of counters (one-to-one correspondence shows which is
// longer), each with its count in front, and the comparison written out as a
// caption underneath so nothing overlaps the counters.
export function CompareVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const { a = 5, b = 3, type = 'greater' } = params;
  const unit = size / 160;
  const maxN = 9;
  const aCount = Math.min(a, maxN), bCount = Math.min(b, maxN);
  const sym = type === 'greater' ? '>' : type === 'less' ? '<' : '=';
  const symColor = type === 'equal' ? c.fill3 : c.fill2;

  const badgeR = 11 * unit;
  const left = size * 0.07;
  const rowStart = left + badgeR * 2 + 7 * unit;
  const cell = Math.min((size * 0.93 - rowStart) / Math.max(aCount, bCount, 1), 20 * unit);
  const r = cell * 0.42;
  const rows = [
    { n: aCount, label: a, y: size * 0.25, color: c.fill1, gid: `${uid}_top` },
    { n: bCount, label: b, y: size * 0.50, color: c.fill2, gid: `${uid}_bottom` },
  ];
  const captionSize = 20 * unit;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
      aria-label={`${a} ${sym} ${b}`}>
      <defs>
        <RichShadow id={`${uid}_rich`} near={0.8} far={2.2} opacityNear={0.20} opacityFar={0.10} />
        <SphereGradient id={`${uid}_top`}    base={c.fill1} cx="32%" cy="28%" />
        <SphereGradient id={`${uid}_bottom`} base={c.fill2} cx="32%" cy="28%" />
      </defs>

      {rows.map((row, i) => (
        <g key={i}>
          <circle cx={left + badgeR} cy={row.y} r={badgeR} fill={row.color} opacity="0.14" />
          <text x={left + badgeR} y={row.y + 1} textAnchor="middle" dominantBaseline="middle"
            fontSize={12 * unit} fontWeight={800} fill={row.color}>{row.label}</text>
          {Array.from({ length: row.n }, (_, k) => (
            <Sphere key={k} x={rowStart + (k + 0.5) * cell} y={row.y} r={r}
              base={row.color} gradientId={row.gid} filterId={`${uid}_rich`} />
          ))}
        </g>
      ))}

      {/* Caption: a  >  b, the symbol in its own tinted badge */}
      <g transform={`translate(${size / 2}, ${size * 0.80})`}>
        <rect x={-16 * unit} y={-16 * unit} width={32 * unit} height={32 * unit} rx={9 * unit}
          fill={symColor} opacity="0.14" />
        <text x={0} y={1} textAnchor="middle" dominantBaseline="middle"
          fontSize={captionSize * 1.15} fontWeight={900} fill={symColor}>{sym}</text>
        <text x={-34 * unit} y={1} textAnchor="middle" dominantBaseline="middle"
          fontSize={captionSize} fontWeight={900} fill={c.fill1}>{a}</text>
        <text x={34 * unit} y={1} textAnchor="middle" dominantBaseline="middle"
          fontSize={captionSize} fontWeight={900} fill={c.fill2}>{b}</text>
      </g>
    </svg>
  );
}

// ─── 7. GeometryVisual ───────────────────────────────────────────────────────
export function GeometryVisual({ visual, params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const type = (visual && visual.type !== 'line' && visual.type !== 'triangle' && visual.type !== 'quadrilateral' && visual.type !== 'circle' && visual.type !== 'angle' && visual.type !== 'point')
    ? (params.type || 'triangle')
    : (params.type || visual?.type || 'triangle');
  const cx = size / 2, cy = size * 0.47;
  const s = size * 0.36;

  // Shared gradient + shadow definitions used by every shape variant below.
  // Body gradient lifts flat fills off the surface; sphereGrad makes vertex
  // dots feel like physical pins instead of stickers.
  const mkSvg = (content, ariaLabel) => (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={ariaLabel}>
      <defs>
        <linearGradient id={`${uid}_body`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor={lighten(c.fill1, 0.85)} />
          <stop offset="100%" stopColor={c.soft1} />
        </linearGradient>
        <linearGradient id={`${uid}_stroke`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={c.fill1} />
          <stop offset="100%" stopColor={darken(c.fill1, 0.15)} />
        </linearGradient>
        <SphereGradient id={`${uid}_vert`} base={c.fill1} cx="32%" cy="28%" />
        <RichShadow id={`${uid}_rich`} near={1.2} far={3} />
        <marker id={`${uid}_al`} markerWidth="7" markerHeight="7" refX="3.5" refY="3.5" orient="auto">
          <path d="M0,0.5 L6.5,3.5 L0,6.5 Z" fill={c.fill1} />
        </marker>
        <marker id={`${uid}_ar`} markerWidth="7" markerHeight="7" refX="3.5" refY="3.5" orient="auto-start-reverse">
          <path d="M0,0.5 L6.5,3.5 L0,6.5 Z" fill={c.fill1} />
        </marker>
        <ShadowDef id={`${uid}_sh`} blur={3} opacity={0.15} />
      </defs>
      {content}
    </svg>
  );

  // Helper: a sphere-look vertex point
  const vertex = (key, x, y, r = 4) => (
    <g key={key} filter={`url(#${uid}_rich)`}>
      <circle cx={x} cy={y} r={r} fill={c.fill1} />
      <circle cx={x} cy={y} r={r} fill={`url(#${uid}_vert)`} />
    </g>
  );

  if (type === 'point') {
    return mkSvg(<>
      <line x1={cx-s} y1={cy} x2={cx+s} y2={cy} stroke={c.line} strokeWidth={1} strokeDasharray="5,4" />
      <line x1={cx} y1={cy-s} x2={cx} y2={cy+s} stroke={c.line} strokeWidth={1} strokeDasharray="5,4" />
      {vertex('p', cx, cy, 8)}
      <text x={cx+14} y={cy-12} fontSize={13} fontWeight={800} fill={c.fill1}>A</text>
    </>, 'xal');
  }
  if (type === 'line') {
    return mkSvg(<>
      <line x1={18} y1={cy} x2={size-18} y2={cy} stroke={c.fill1} strokeWidth={2.5}
        markerStart={`url(#${uid}_ar)`} markerEnd={`url(#${uid}_al)`} />
      <text x={size/2} y={cy+20} textAnchor="middle" fontSize={10} fill={c.dim}>xêz</text>
    </>, 'xêz');
  }
  if (type === 'segment') {
    return mkSvg(<>
      <line x1={22} y1={cy} x2={size-22} y2={cy} stroke={`url(#${uid}_stroke)`} strokeWidth={3} strokeLinecap="round" />
      {vertex('a', 22, cy, 5.5)}
      {vertex('b', size-22, cy, 5.5)}
      <text x={22} y={cy-14} fontSize={11} fontWeight={800} fill={c.fill1}>A</text>
      <text x={size-22} y={cy-14} fontSize={11} fontWeight={800} fill={c.fill1}>B</text>
    </>, 'beş');
  }
  if (type === 'ray') {
    return mkSvg(<>
      <line x1={22} y1={cy} x2={size-18} y2={cy} stroke={`url(#${uid}_stroke)`} strokeWidth={3} strokeLinecap="round"
        markerEnd={`url(#${uid}_al)`} />
      {vertex('a', 22, cy, 5.5)}
      <text x={22} y={cy-14} fontSize={11} fontWeight={800} fill={c.fill1}>A</text>
    </>, 'tîr');
  }
  if (type === 'parallel') {
    return mkSvg(<>
      <line x1={22} y1={size*0.32} x2={size-22} y2={size*0.32} stroke={c.fill1} strokeWidth={2.5} />
      <line x1={22} y1={size*0.58} x2={size-22} y2={size*0.58} stroke={c.fill2} strokeWidth={2.5} />
      {/* Tick marks for parallel */}
      <line x1={size*0.45} y1={size*0.28} x2={size*0.48} y2={size*0.36} stroke={c.fill1} strokeWidth={2} />
      <line x1={size*0.45} y1={size*0.54} x2={size*0.48} y2={size*0.62} stroke={c.fill2} strokeWidth={2} />
      <text x={size*0.88} y={size*0.30} fontSize={11} fontStyle="italic" fill={c.fill1}>ℓ₁</text>
      <text x={size*0.88} y={size*0.56} fontSize={11} fontStyle="italic" fill={c.fill2}>ℓ₂</text>
      <text x={size/2} y={size*0.80} textAnchor="middle" fontSize={12} fontWeight={700} fill={c.dim}>ℓ₁ ∥ ℓ₂</text>
    </>, 'paralel xêz');
  }
  if (type === 'perpendicular') {
    return mkSvg(<>
      <line x1={18} y1={cy} x2={size-18} y2={cy} stroke={c.fill1} strokeWidth={2.5} />
      <line x1={cx} y1={size*0.14} x2={cx} y2={size*0.82} stroke={c.fill2} strokeWidth={2.5} />
      <rect x={cx} y={cy} width={10} height={10} fill="none" stroke={c.fill2} strokeWidth={1.8} />
      <text x={size/2} y={size*0.88} textAnchor="middle" fontSize={12} fontWeight={700} fill={c.dim}>90°</text>
    </>, 'perpendîkular');
  }
  if (type === 'triangle' || type === 'equilateral' || type === 'right_triangle') {
    let pts;
    if (type === 'equilateral') {
      pts = [[cx, cy-s*1.0], [cx-s*1.0, cy+s*0.62], [cx+s*1.0, cy+s*0.62]];
    } else if (type === 'right_triangle') {
      pts = [[cx-s*0.85, cy-s*0.75], [cx-s*0.85, cy+s*0.65], [cx+s*0.85, cy+s*0.65]];
    } else {
      pts = [[cx, cy-s*0.95], [cx-s*0.92, cy+s*0.72], [cx+s*0.78, cy+s*0.72]];
    }
    const pStr = pts.map(p => p.join(',')).join(' ');
    return mkSvg(<>
      <g filter={`url(#${uid}_rich)`}>
        <polygon points={pStr} fill={`url(#${uid}_body)`} stroke={`url(#${uid}_stroke)`} strokeWidth={2.5}
          strokeLinejoin="round" />
      </g>
      {type === 'right_triangle' && (
        <rect x={pts[0][0]} y={pts[0][1]} width={12} height={12}
          fill="none" stroke={c.fill1} strokeWidth={1.8} strokeOpacity={0.55} />
      )}
      {pts.map((p, i) => vertex(`v${i}`, p[0], p[1], 4.5))}
    </>, 'sêgoşe');
  }
  if (type === 'quadrilateral' || type === 'square' || type === 'rectangle') {
    const rw = type === 'rectangle' ? s * 1.55 : s * 0.92;
    const rh = s * 0.92;
    return mkSvg(<>
      <g filter={`url(#${uid}_rich)`}>
        <rect x={cx-rw/2} y={cy-rh/2} width={rw} height={rh}
          fill={`url(#${uid}_body)`} stroke={`url(#${uid}_stroke)`} strokeWidth={2.5}
          rx={type === 'quadrilateral' ? 4 : 0} strokeLinejoin="round" />
      </g>
      {type === 'square' && <>
        {/* Equal side ticks */}
        <line x1={cx-rw/2+4} y1={cy-4} x2={cx-rw/2+11} y2={cy-4} stroke={c.fill1} strokeWidth={2} strokeLinecap="round" />
        <line x1={cx+rw/2-11} y1={cy-4} x2={cx+rw/2-4} y2={cy-4} stroke={c.fill1} strokeWidth={2} strokeLinecap="round" />
        <line x1={cx-4} y1={cy-rh/2+4} x2={cx-4} y2={cy-rh/2+11} stroke={c.fill1} strokeWidth={2} strokeLinecap="round" />
        <line x1={cx-4} y1={cy+rh/2-11} x2={cx-4} y2={cy+rh/2-4} stroke={c.fill1} strokeWidth={2} strokeLinecap="round" />
      </>}
      {/* Corner vertices */}
      {vertex('c1', cx-rw/2, cy-rh/2, 3.5)}
      {vertex('c2', cx+rw/2, cy-rh/2, 3.5)}
      {vertex('c3', cx+rw/2, cy+rh/2, 3.5)}
      {vertex('c4', cx-rw/2, cy+rh/2, 3.5)}
    </>, type === 'square' ? 'çargoşe' : type === 'rectangle' ? 'tîrêj' : 'çargoşe');
  }
  if (type === 'circle') {
    const { showRadius = true } = params;
    return mkSvg(<>
      <g filter={`url(#${uid}_rich)`}>
        <circle cx={cx} cy={cy} r={s} fill={`url(#${uid}_body)`}
          stroke={`url(#${uid}_stroke)`} strokeWidth={2.5} />
      </g>
      {/* Glossy specular arc top-left */}
      <path
        d={`M ${cx - s * 0.55},${cy - s * 0.65} A ${s * 0.85},${s * 0.85} 0 0,1 ${cx + s * 0.20},${cy - s * 0.85}`}
        stroke="#fff" strokeWidth={s * 0.14} strokeLinecap="round"
        fill="none" opacity="0.30"
      />
      {vertex('o', cx, cy, 4)}
      {showRadius && <>
        <line x1={cx} y1={cy} x2={cx+s} y2={cy} stroke={c.fill2} strokeWidth={1.8} strokeDasharray="5,3" />
        <text x={cx+s/2} y={cy-10} textAnchor="middle" fontSize={11} fontWeight={800} fill={c.fill2}>r</text>
      </>}
    </>, 'xemberî');
  }
  if (type === 'angle') {
    const { degrees = 60 } = params;
    const rad = (degrees * Math.PI) / 180;
    // Vertex sits bottom-left (centre for obtuse angles) so the arms can
    // use most of the canvas.
    const ox = degrees > 90 ? size * 0.5 : size * 0.20, oy = size * 0.76;
    const len = size * (degrees > 90 ? 0.40 : 0.62);
    const x2 = ox + len, y2 = oy;
    const x3 = ox + len * Math.cos(-rad), y3 = oy + len * Math.sin(-rad);
    const arcR = len * 0.38;
    const large = degrees > 180 ? 1 : 0;
    // Filled arc using coral gradient so the angle wedge reads as a real surface
    return mkSvg(<>
      <line x1={ox} y1={oy} x2={x2} y2={y2} stroke={`url(#${uid}_stroke)`} strokeWidth={3.2} strokeLinecap="round" />
      <line x1={ox} y1={oy} x2={x3} y2={y3} stroke={`url(#${uid}_stroke)`} strokeWidth={3.2} strokeLinecap="round" />
      <path
        d={`M${ox},${oy} L${ox+arcR},${oy} A${arcR},${arcR} 0 ${large},1 ${ox+arcR*Math.cos(-rad)},${oy+arcR*Math.sin(-rad)} Z`}
        fill={c.soft2}
        stroke={c.fill2}
        strokeWidth={1.5}
        opacity={0.92}
      />
      {vertex('o', ox, oy, 6)}
      <text x={ox + arcR * 1.55 * Math.cos(-rad / 2)}
        y={oy + arcR * 1.55 * Math.sin(-rad / 2) + 4}
        textAnchor="middle" fontSize={15} fontWeight={800} fill={c.fill2}>{degrees}°</text>
    </>, `${degrees} derece goşe`);
  }
  // Default triangle
  const pts = [[cx, cy-s], [cx-s, cy+s*0.72], [cx+s, cy+s*0.72]];
  return mkSvg(<>
    <g filter={`url(#${uid}_rich)`}>
      <polygon points={pts.map(p=>p.join(',')).join(' ')}
        fill={`url(#${uid}_body)`} stroke={`url(#${uid}_stroke)`} strokeWidth={2.5}
        strokeLinejoin="round" />
    </g>
    {pts.map((p, i) => vertex(`v${i}`, p[0], p[1], 4))}
  </>, 'şekl');
}

// ─── 8. Geometry3DVisual ─────────────────────────────────────────────────────
// Polished: 3D primitives wear face-specific gradients (top brightest, sides
// graduated) plus a top-left specular highlight that sells the "lit from
// above" illusion. Ground shadow ellipses anchor each shape on the surface.
export function Geometry3DVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const { shape = 'cube' } = params;
  const cx = size / 2, cy = size * 0.48, s = size * 0.30;

  // Pre-compute palette stops for face shading
  const topLight  = lighten(c.fill1, 0.35);
  const sideMid   = c.fill1;
  const sideDark  = darken(c.fill1, 0.20);

  const mkSvg = (content) => (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={shape}>
      <defs>
        <RichShadow id={`${uid}_rich`} near={1.5} far={4} />
        <linearGradient id={`${uid}_top`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={topLight} />
          <stop offset="100%" stopColor={sideMid} />
        </linearGradient>
        <linearGradient id={`${uid}_side`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor={sideMid} />
          <stop offset="100%" stopColor={sideDark} />
        </linearGradient>
        <linearGradient id={`${uid}_front`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={lighten(c.fill1, 0.15)} />
          <stop offset="100%" stopColor={sideMid} />
        </linearGradient>
        <radialGradient id={`${uid}_sphere`} cx="32%" cy="28%" r="68%">
          <stop offset="0%"   stopColor="#ffffff" stopOpacity="0.85" />
          <stop offset="20%"  stopColor={topLight} stopOpacity="0.85" />
          <stop offset="60%"  stopColor={c.fill1} />
          <stop offset="100%" stopColor={sideDark} />
        </radialGradient>
      </defs>
      {/* Ground shadow */}
      <ellipse cx={cx} cy={cy + s + 8} rx={s * 0.95} ry={s * 0.14}
        fill="#000" opacity="0.14" />
      {content}
    </svg>
  );

  if (shape === 'cube') {
    const d = s * 0.52;
    const x1 = cx - s,  y1 = cy - s * 0.65 + d;     // front-top-left
    const x2 = cx + s,  y2 = y1;                    // front-top-right
    const x3 = cx + s,  y3 = cy + s * 0.65 + d;     // front-bottom-right
    const x4 = cx - s,  y4 = y3;                    // front-bottom-left
    const x1b = x1 + d, y1b = y1 - d;               // back-top-left
    const x2b = x2 + d, y2b = y2 - d;               // back-top-right
    const x3b = x3 + d, y3b = y3 - d;               // back-bottom-right
    return mkSvg(<>
      <g filter={`url(#${uid}_rich)`}>
        {/* Right face */}
        <polygon points={`${x2},${y2} ${x2b},${y2b} ${x3b},${y3b} ${x3},${y3}`}
          fill={`url(#${uid}_side)`} stroke={sideDark} strokeWidth={0.6} strokeLinejoin="round" />
        {/* Top face */}
        <polygon points={`${x1},${y1} ${x1b},${y1b} ${x2b},${y2b} ${x2},${y2}`}
          fill={`url(#${uid}_top)`} stroke={sideMid} strokeWidth={0.6} strokeLinejoin="round" />
        {/* Front face */}
        <polygon points={`${x1},${y1} ${x2},${y2} ${x3},${y3} ${x4},${y4}`}
          fill={`url(#${uid}_front)`} stroke={sideMid} strokeWidth={0.6} strokeLinejoin="round" />
      </g>
      {/* Top-left specular */}
      <polygon points={`${x1+3},${y1+3} ${x1+s*0.5},${y1+3} ${x1+s*0.5},${y1+8} ${x1+3},${y1+8}`}
        fill="#fff" opacity="0.18" />
    </>);
  }
  if (shape === 'sphere') {
    return mkSvg(<>
      <g filter={`url(#${uid}_rich)`}>
        <circle cx={cx} cy={cy} r={s * 1.05} fill={`url(#${uid}_sphere)`} />
      </g>
      {/* Specular highlight */}
      <ellipse cx={cx - s * 0.34} cy={cy - s * 0.40} rx={s * 0.30} ry={s * 0.18}
        fill="#fff" opacity="0.55" />
      {/* Equator hint (dashed) */}
      <ellipse cx={cx} cy={cy} rx={s * 1.05} ry={s * 0.32}
        fill="none" stroke={sideDark} strokeWidth={1} strokeDasharray="4,3" opacity="0.40" />
    </>);
  }
  if (shape === 'cylinder') {
    return mkSvg(<>
      <g filter={`url(#${uid}_rich)`}>
        {/* Side */}
        <rect x={cx - s * 0.85} y={cy - s} width={s * 1.7} height={s * 2}
          fill={`url(#${uid}_side)`} stroke={sideMid} strokeWidth={0.8} />
        {/* Bottom ellipse (visible behind body for closed look) */}
        <ellipse cx={cx} cy={cy + s} rx={s * 0.85} ry={s * 0.28}
          fill={sideDark} stroke={sideDark} strokeWidth={0.8} />
        {/* Top ellipse */}
        <ellipse cx={cx} cy={cy - s} rx={s * 0.85} ry={s * 0.28}
          fill={`url(#${uid}_top)`} stroke={sideMid} strokeWidth={0.8} />
      </g>
      {/* Vertical specular stripe on the left side of body */}
      <rect x={cx - s * 0.70} y={cy - s + 4} width={s * 0.18} height={s * 2 - 8}
        fill="#fff" opacity="0.18" rx={2} />
    </>);
  }
  if (shape === 'cone') {
    return mkSvg(<>
      <g filter={`url(#${uid}_rich)`}>
        <polygon points={`${cx},${cy - s * 1.15} ${cx - s},${cy + s * 0.82} ${cx + s},${cy + s * 0.82}`}
          fill={`url(#${uid}_front)`} stroke={sideMid} strokeWidth={0.8} strokeLinejoin="round" />
        <ellipse cx={cx} cy={cy + s * 0.82} rx={s} ry={s * 0.30}
          fill={`url(#${uid}_top)`} stroke={sideMid} strokeWidth={0.8} />
      </g>
      {/* Specular stripe down the left slope */}
      <path d={`M ${cx - s * 0.6},${cy - s * 0.4} L ${cx - s * 0.85},${cy + s * 0.6}`}
        stroke="#fff" strokeWidth={s * 0.10} strokeLinecap="round" opacity="0.25" />
    </>);
  }
  // Prism (default)
  return mkSvg(<>
    <g filter={`url(#${uid}_rich)`}>
      <polygon
        points={`${cx},${cy - s * 0.95} ${cx - s * 0.85},${cy + s * 0.52} ${cx + s * 0.85},${cy + s * 0.52}`}
        fill={`url(#${uid}_front)`} stroke={sideMid} strokeWidth={0.8} strokeLinejoin="round" />
      <polygon
        points={`${cx + s * 0.85},${cy + s * 0.52} ${cx + s * 0.85 + s * 0.4},${cy + s * 0.18} ${cx + s * 0.4},${cy - s * 1.3} ${cx},${cy - s * 0.95}`}
        fill={`url(#${uid}_side)`} stroke={sideMid} strokeWidth={0.8} strokeLinejoin="round" />
    </g>
  </>);
}

// ─── 9. CoordinateVisual ─────────────────────────────────────────────────────
// Polished: axes wear a subtle gradient stroke that fades toward the arrowhead;
// the plotted point is a glossy sphere; dashed coordinate lines pulse softly
// toward each axis to show projection.
export function CoordinateVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const { direction = null, showGrid = false } = params;
  const w = size, h = size;
  const cx = w * 0.40, cy = h * 0.55;

  // Shared sphere highlight used by points
  const SphereDot = ({ x, y, r = 7, color = c.fill1 }) => (
    <g filter={`url(#${uid}_rich)`}>
      <circle cx={x} cy={y} r={r} fill={color} />
      <circle cx={x} cy={y} r={r} fill={`url(#${uid}_sphereGen)`} />
      <ellipse cx={x - r * 0.32} cy={y - r * 0.38} rx={r * 0.32} ry={r * 0.20}
        fill="#fff" opacity="0.55" />
    </g>
  );

  if (direction === 'inside_outside') {
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img" aria-label="nav û derve">
        <defs>
          <RichShadow id={`${uid}_rich`} near={1} far={2.5} />
          <SphereGradient id={`${uid}_sphereGen`} base={c.fill1} cx="32%" cy="28%" />
          <radialGradient id={`${uid}_inner`} cx="40%" cy="36%" r="65%">
            <stop offset="0%" stopColor={lighten(c.fill1, 0.40)} />
            <stop offset="100%" stopColor={c.fill1} />
          </radialGradient>
        </defs>
        <circle cx={cx} cy={cy} r={44} fill="none" stroke={c.line} strokeWidth={2} strokeDasharray="5,3" />
        <g filter={`url(#${uid}_rich)`}>
          <circle cx={cx} cy={cy} r={24} fill={`url(#${uid}_inner)`} stroke={darken(c.fill1, 0.1)} strokeWidth={2} />
        </g>
        <text x={cx} y={cy + 4} textAnchor="middle" fontSize={10} fontWeight={800} fill={c.white}>nav</text>
        <text x={cx + 54} y={cy - 22} fontSize={10} fontWeight={700} fill={c.muted}>derve</text>
        <line x1={cx + 24} y1={cy - 10} x2={cx + 46} y2={cy - 24} stroke={c.muted} strokeWidth={1} strokeDasharray="3,2" />
      </svg>
    );
  }
  if (direction === 'near_far') {
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img" aria-label="nêzîk û dûr">
        <defs>
          <RichShadow id={`${uid}_rich`} near={1} far={2.5} />
          <SphereGradient id={`${uid}_sphereGen`} base={c.fill1} cx="32%" cy="28%" />
          <SphereGradient id={`${uid}_sphereFar`} base={c.fill1} cx="32%" cy="28%" />
        </defs>
        <SphereDot x={cx} y={cy} r={13} />
        <g opacity="0.42"><SphereDot x={cx + 64} y={cy} r={13} /></g>
        <line x1={cx + 14} y1={cy} x2={cx + 50} y2={cy} stroke={c.fill2} strokeWidth={2} strokeDasharray="4,3" />
        <text x={cx} y={cy + 28} textAnchor="middle" fontSize={10} fontWeight={800} fill={c.fill1}>nêzîk</text>
        <text x={cx + 64} y={cy + 28} textAnchor="middle" fontSize={10} fontWeight={700} fill={c.muted}>dûr</text>
      </svg>
    );
  }

  const gridElems = [];
  if (showGrid) {
    for (let i = -3; i <= 3; i++) {
      gridElems.push(
        <line key={`gx${i}`} x1={cx + i * 22} y1={12} x2={cx + i * 22} y2={h - 12}
          stroke={c.line} strokeWidth={0.5} opacity={0.4} />,
        <line key={`gy${i}`} x1={12} y1={cy + i * 22} x2={w - 12} y2={cy + i * 22}
          stroke={c.line} strokeWidth={0.5} opacity={0.4} />
      );
    }
  }

  const dirElems = [];
  if (direction === 'above_below') {
    dirElems.push(
      <text key="above" x={cx + 8} y={cy - 28} fontSize={11} fontWeight={800} fill={c.fill1}>↑ Ser</text>,
      <text key="below" x={cx + 8} y={cy + 34} fontSize={11} fontWeight={800} fill={c.fill2}>↓ Bin</text>
    );
  } else if (direction === 'left_right') {
    dirElems.push(
      <text key="right" x={cx + 22} y={cy - 10} fontSize={11} fontWeight={800} fill={c.fill1}>Rast →</text>,
      <text key="left" x={14} y={cy - 10} fontSize={11} fontWeight={800} fill={c.fill2}>← Çep</text>
    );
  } else {
    // Default: plotted point at (2, 3) with projection lines
    const px = cx + 44, py = cy - 44;
    dirElems.push(
      <line key="dx" x1={px} y1={cy} x2={px} y2={py} stroke={c.fill2} strokeWidth={1.2} strokeDasharray="3,2" opacity={0.55} />,
      <line key="dy" x1={cx} y1={py} x2={px} y2={py} stroke={c.fill2} strokeWidth={1.2} strokeDasharray="3,2" opacity={0.55} />,
      <SphereDot key="dot" x={px} y={py} r={7} color={c.fill2} />,
      <g key="lbl" transform={`translate(${px + 12}, ${py - 12})`}>
        <rect x={-2} y={-9} width={32} height={16} rx={8} fill={c.fill2} opacity="0.12" />
        <text x={14} y={2} textAnchor="middle" dominantBaseline="middle"
          fontSize={9} fontWeight={800} fill={c.fill2}>(2,3)</text>
      </g>
    );
  }

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img" aria-label="koordînat">
      <defs>
        <RichShadow id={`${uid}_rich`} near={1} far={2.5} />
        <SphereGradient id={`${uid}_sphereGen`} base={c.fill2} cx="32%" cy="28%" />
        <linearGradient id={`${uid}_axisX`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={c.line} />
          <stop offset="80%" stopColor={c.muted} />
        </linearGradient>
        <linearGradient id={`${uid}_axisY`} x1="0%" y1="100%" x2="0%" y2="0%">
          <stop offset="0%" stopColor={c.line} />
          <stop offset="80%" stopColor={c.muted} />
        </linearGradient>
        <marker id={`${uid}_ca`} markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
          <path d="M0,0.5 L5.5,3 L0,5.5 Z" fill={c.muted} />
        </marker>
      </defs>
      {gridElems}
      {/* Axes with subtle baseline shadow */}
      <line x1={14} y1={cy + 1.5} x2={w - 12} y2={cy + 1.5} stroke="#000" strokeWidth={0.6} opacity="0.08" />
      <line x1={14} y1={cy} x2={w - 12} y2={cy} stroke={`url(#${uid}_axisX)`} strokeWidth={2}
        strokeLinecap="round" markerEnd={`url(#${uid}_ca)`} />
      <line x1={cx} y1={h - 14} x2={cx} y2={12} stroke={`url(#${uid}_axisY)`} strokeWidth={2}
        strokeLinecap="round" markerEnd={`url(#${uid}_ca)`} />
      <text x={w - 10} y={cy - 5} fontSize={10} fontWeight={700} fill={c.muted}>x</text>
      <text x={cx + 5} y={16} fontSize={10} fontWeight={700} fill={c.muted}>y</text>
      {/* Origin dot */}
      <circle cx={cx} cy={cy} r={2.5} fill={c.muted} />
      {dirElems}
    </svg>
  );
}

// ─── 10. SymmetryVisual ──────────────────────────────────────────────────────
// Polished: two mirror-image wings — left wears the logo teal gradient, right
// the coral gradient — so symmetry reads as "the same shape, reflected across
// the axis". A glowing axis underscores the reflection plane.
export function SymmetryVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const cx = size / 2, h = size;
  const showLabel = params.label !== false;
  return (
    <svg width={size} height={h} viewBox={`0 0 ${size} ${h}`} role="img" aria-label="sîmetrî">
      <defs>
        <RichShadow id={`${uid}_rich`} near={1.5} far={3.5} />
        <radialGradient id={`${uid}_leftG`} cx="65%" cy="40%" r="80%">
          <stop offset="0%"   stopColor={lighten(c.fill1, 0.25)} />
          <stop offset="100%" stopColor={c.fill1} />
        </radialGradient>
        <radialGradient id={`${uid}_rightG`} cx="35%" cy="40%" r="80%">
          <stop offset="0%"   stopColor={lighten(c.fill2, 0.25)} />
          <stop offset="100%" stopColor={c.fill2} />
        </radialGradient>
        <linearGradient id={`${uid}_axisG`} x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%"   stopColor={c.muted} stopOpacity="0" />
          <stop offset="15%"  stopColor={c.fill1} stopOpacity="0.6" />
          <stop offset="50%"  stopColor={c.fill1} />
          <stop offset="85%"  stopColor={c.fill1} stopOpacity="0.6" />
          <stop offset="100%" stopColor={c.muted} stopOpacity="0" />
        </linearGradient>
      </defs>
      <g filter={`url(#${uid}_rich)`}>
        {/* Left wing */}
        <path d={`M${cx},${h * 0.22} Q${cx - 48},${h * 0.06} ${cx - 58},${h * 0.44} Q${cx - 48},${h * 0.70} ${cx},${h * 0.74}`}
          fill={`url(#${uid}_leftG)`} stroke={darken(c.fill1, 0.10)} strokeWidth={1.2} strokeOpacity="0.5"
          strokeLinejoin="round" />
        {/* Right wing — mirror */}
        <path d={`M${cx},${h * 0.22} Q${cx + 48},${h * 0.06} ${cx + 58},${h * 0.44} Q${cx + 48},${h * 0.70} ${cx},${h * 0.74}`}
          fill={`url(#${uid}_rightG)`} stroke={darken(c.fill2, 0.10)} strokeWidth={1.2} strokeOpacity="0.5"
          strokeLinejoin="round" />
      </g>
      {/* Wing highlights (top-left specular) */}
      <ellipse cx={cx - 32} cy={h * 0.26} rx="14" ry="5" fill="#fff" opacity="0.30" transform={`rotate(-25 ${cx - 32} ${h * 0.26})`} />
      <ellipse cx={cx + 32} cy={h * 0.26} rx="14" ry="5" fill="#fff" opacity="0.30" transform={`rotate(25 ${cx + 32} ${h * 0.26})`} />
      {/* Axis — gradient stroke fades at top/bottom */}
      <line x1={cx} y1={h * 0.06} x2={cx} y2={h * 0.88}
        stroke={`url(#${uid}_axisG)`} strokeWidth={2.4} strokeDasharray="5,3" strokeLinecap="round" />
      {/* Axis end markers */}
      <circle cx={cx} cy={h * 0.06} r="2" fill={c.fill1} opacity="0.6" />
      <circle cx={cx} cy={h * 0.88} r="2" fill={c.fill1} opacity="0.6" />
      {showLabel && (
        <g transform={`translate(${cx}, ${h * 0.94})`}>
          <rect x="-30" y="-9" width="60" height="16" rx="8" fill={c.fill1} opacity="0.10" />
          <text x="0" y="1" textAnchor="middle" dominantBaseline="middle"
            fontSize={10} fontWeight={800} fill={c.fill1}>sîmetrî</text>
        </g>
      )}
    </svg>
  );
}

// ─── 11. RulerVisual ─────────────────────────────────────────────────────────
// Polished: a real plastic-with-coloured-edge ruler. The body has a vertical
// gradient (paler top, slightly darker bottom — the "lit-from-above" cue);
// the brand stripe runs along the top edge; tick hierarchy is three-deep
// (major every cm, half-cm medium, mm short); the bottom 1px shadow grounds
// it on the page.
export function RulerVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const { length = 5, unit = 'cm' } = params;
  const startX = 12, endX = size - 12;
  const rulerW = endX - startX;
  const rulerH = size * 0.32, rulerY = size * 0.36;
  const tickCount = Math.min(length, 10);
  const tickGap = rulerW / tickCount;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${length}${unit} pîvan`}>
      <defs>
        <RichShadow id={`${uid}_rich`} near={1.5} far={3.5} />
        <linearGradient id={`${uid}_body`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor={lighten(c.bg, 0.15)} />
          <stop offset="50%"  stopColor={c.bg} />
          <stop offset="100%" stopColor={darken(c.bg, 0.08)} />
        </linearGradient>
        <linearGradient id={`${uid}_stripe`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor={lighten(c.fill1, 0.18)} />
          <stop offset="100%" stopColor={c.fill1} />
        </linearGradient>
      </defs>

      {/* Body — outer drop shadow grounds the ruler */}
      <g filter={`url(#${uid}_rich)`}>
        <rect x={startX} y={rulerY} width={rulerW} height={rulerH}
          fill={`url(#${uid}_body)`} stroke={darken(c.fill1, 0.1)} strokeWidth={0.8}
          strokeOpacity={0.35} rx={4} />
      </g>

      {/* Top coloured stripe (brand band) */}
      <rect x={startX} y={rulerY} width={rulerW} height={rulerH * 0.22}
        fill={`url(#${uid}_stripe)`} rx={4} />
      {/* Stripe inner highlight */}
      <rect x={startX + 2} y={rulerY + 1.5} width={rulerW - 4} height={rulerH * 0.06}
        fill="#fff" opacity="0.30" rx={2} />

      {/* Subtle separator under the stripe */}
      <line x1={startX} y1={rulerY + rulerH * 0.22} x2={endX} y2={rulerY + rulerH * 0.22}
        stroke={darken(c.fill1, 0.15)} strokeWidth={0.4} opacity="0.40" />

      {/* Ticks: major (cm) every gap, medium at half-cm, short at every mm */}
      {(() => {
        const out = [];
        const stripeBottom = rulerY + rulerH * 0.22;
        const tickBaseY = rulerY + rulerH - 2;
        const subTicksPerCm = 10;
        const subGap = tickGap / subTicksPerCm;

        for (let i = 0; i <= tickCount; i++) {
          const x = startX + i * tickGap;
          const isMajor = true;
          const tickH = rulerH * 0.55;
          out.push(
            <line key={`maj${i}`}
              x1={x} y1={tickBaseY - tickH} x2={x} y2={tickBaseY}
              stroke={darken(c.fill1, 0.05)} strokeWidth={1.4} strokeLinecap="round" />
          );
          out.push(
            <text key={`num${i}`} x={x} y={rulerY + rulerH + 12}
              textAnchor="middle" fontSize={9} fontWeight={700} fill={c.dim}>
              {i}
            </text>
          );
          // Medium + sub ticks between this cm and next (skip on last)
          if (i < tickCount) {
            for (let j = 1; j < subTicksPerCm; j++) {
              const sx = x + j * subGap;
              if (sx > endX - 1) break;
              const isMedium = j === 5;
              const subH = isMedium ? rulerH * 0.30 : rulerH * 0.16;
              out.push(
                <line key={`s${i}_${j}`}
                  x1={sx} y1={tickBaseY - subH} x2={sx} y2={tickBaseY}
                  stroke={c.muted} strokeWidth={isMedium ? 0.8 : 0.5}
                  opacity={isMedium ? 0.85 : 0.55}
                  strokeLinecap="round" />
              );
            }
          }
        }
        return out;
      })()}

      {/* Unit label inside the brand stripe */}
      <text x={endX - 6} y={rulerY + rulerH * 0.15}
        fontSize={8.5} fontWeight={800} fill="#fff" textAnchor="end"
        letterSpacing="0.04em">
        {unit.toUpperCase()}
      </text>

      {/* Subtle reflection highlight on body (top edge) */}
      <rect x={startX + 3} y={rulerY + rulerH * 0.28} width={rulerW - 6} height={2}
        fill="#fff" opacity="0.18" rx={1} />
    </svg>
  );
}

// ─── 12. ScaleVisual ─────────────────────────────────────────────────────────
// Polished: classic balance scale. Brass-tinted gradient post, 3D base plate,
// rounded beam with shadow, dish-shaped pans (ellipse with thickness), glossy
// pivot sphere. Balanced state uses green beam; tilted state uses logo coral.
export function ScaleVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const { balanced = true, leftLabel = '', rightLabel = '' } = params;
  const cx = size / 2, baseY = size * 0.86, postH = size * 0.50;
  const tilt = balanced ? 0 : 12;
  const beamColor = balanced ? c.fill3 : c.fill2;
  // Pan positions (rotate around pivot)
  const beamLen = size * 0.34;
  const leftEndX = cx - beamLen, leftEndY = baseY - postH + tilt;
  const rightEndX = cx + beamLen, rightEndY = baseY - postH - tilt;
  const panY = (y) => Math.min(y + 22, size - 18);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
      aria-label={balanced ? 'hevseng' : 'nehevseng'}>
      <defs>
        <RichShadow id={`${uid}_rich`} near={1.2} far={3.5} />
        <linearGradient id={`${uid}_post`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor={lighten(c.muted, 0.20)} />
          <stop offset="50%"  stopColor={c.muted} />
          <stop offset="100%" stopColor={darken(c.muted, 0.20)} />
        </linearGradient>
        <linearGradient id={`${uid}_base`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor={lighten(c.muted, 0.15)} />
          <stop offset="100%" stopColor={darken(c.muted, 0.15)} />
        </linearGradient>
        <linearGradient id={`${uid}_beam`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor={lighten(beamColor, 0.20)} />
          <stop offset="100%" stopColor={darken(beamColor, 0.10)} />
        </linearGradient>
        <SphereGradient id={`${uid}_pivot`} base={beamColor} cx="32%" cy="28%" />
        <radialGradient id={`${uid}_panL`} cx="50%" cy="35%" r="65%">
          <stop offset="0%"   stopColor={lighten(c.fill1, 0.30)} />
          <stop offset="100%" stopColor={darken(c.fill1, 0.15)} />
        </radialGradient>
        <radialGradient id={`${uid}_panR`} cx="50%" cy="35%" r="65%">
          <stop offset="0%"   stopColor={lighten(c.fill2, 0.30)} />
          <stop offset="100%" stopColor={darken(c.fill2, 0.15)} />
        </radialGradient>
      </defs>

      {/* Ground shadow */}
      <ellipse cx={cx} cy={baseY + 11} rx={42} ry={3.5} fill="#000" opacity="0.16" />

      {/* Base plate — 3D look with top face + dark side */}
      <rect x={cx - 30} y={baseY + 4} width={60} height={4}
        fill={darken(c.muted, 0.18)} rx={2} />
      <rect x={cx - 30} y={baseY} width={60} height={6}
        fill={`url(#${uid}_base)`} rx={2.5} />
      <rect x={cx - 28} y={baseY + 1} width={56} height={1.4}
        fill="#fff" opacity="0.20" rx={0.7} />

      {/* Vertical post */}
      <rect x={cx - 3} y={baseY - postH} width={6} height={postH}
        fill={`url(#${uid}_post)`} rx={1.5} />
      <rect x={cx - 2.5} y={baseY - postH + 4} width={1.4} height={postH - 8}
        fill="#fff" opacity="0.30" rx={0.7} />

      {/* Beam (rotates) — wider rounded bar */}
      <g transform={`rotate(${balanced ? 0 : -8} ${cx} ${baseY - postH})`}>
        <line x1={cx - beamLen} y1={baseY - postH} x2={cx + beamLen} y2={baseY - postH}
          stroke="#000" strokeWidth={5} strokeLinecap="round" opacity="0.18" transform="translate(0,1.5)" />
        <line x1={cx - beamLen} y1={baseY - postH} x2={cx + beamLen} y2={baseY - postH}
          stroke={`url(#${uid}_beam)`} strokeWidth={4} strokeLinecap="round" />
        <line x1={cx - beamLen + 4} y1={baseY - postH - 1} x2={cx + beamLen - 4} y2={baseY - postH - 1}
          stroke="#fff" strokeWidth={1} strokeLinecap="round" opacity="0.40" />
      </g>

      {/* Pivot — glossy sphere */}
      <g filter={`url(#${uid}_rich)`}>
        <circle cx={cx} cy={baseY - postH} r={6} fill={beamColor} />
        <circle cx={cx} cy={baseY - postH} r={6} fill={`url(#${uid}_pivot)`} />
      </g>
      <ellipse cx={cx - 2} cy={baseY - postH - 2} rx="1.8" ry="1"
        fill="#fff" opacity="0.65" />

      {/* Left chain + pan */}
      <line x1={leftEndX} y1={leftEndY} x2={leftEndX} y2={leftEndY + 22}
        stroke={darken(c.muted, 0.10)} strokeWidth={1.5} strokeLinecap="round" />
      <g filter={`url(#${uid}_rich)`}>
        {/* Pan: ellipse with subtle 3D rim */}
        <ellipse cx={leftEndX} cy={leftEndY + 25} rx={26} ry={4}
          fill={darken(c.fill1, 0.20)} />
        <ellipse cx={leftEndX} cy={leftEndY + 23} rx={26} ry={8}
          fill={`url(#${uid}_panL)`} stroke={darken(c.fill1, 0.15)} strokeWidth={0.8} />
        <ellipse cx={leftEndX} cy={leftEndY + 22} rx={22} ry={5.5}
          fill="none" stroke="#fff" strokeWidth={0.7} opacity="0.40" />
      </g>
      {leftLabel && (
        <g transform={`translate(${leftEndX}, ${panY(leftEndY + 18)})`}>
          <rect x="-18" y="-9" width="36" height="16" rx="8" fill={c.fill1} opacity="0.12" />
          <text x="0" y="1" textAnchor="middle" dominantBaseline="middle"
            fontSize={9} fontWeight={800} fill={c.fill1}>{leftLabel}</text>
        </g>
      )}

      {/* Right chain + pan */}
      <line x1={rightEndX} y1={rightEndY} x2={rightEndX} y2={rightEndY + 22}
        stroke={darken(c.muted, 0.10)} strokeWidth={1.5} strokeLinecap="round" />
      <g filter={`url(#${uid}_rich)`}>
        <ellipse cx={rightEndX} cy={rightEndY + 25} rx={26} ry={4}
          fill={darken(c.fill2, 0.20)} />
        <ellipse cx={rightEndX} cy={rightEndY + 23} rx={26} ry={8}
          fill={`url(#${uid}_panR)`} stroke={darken(c.fill2, 0.15)} strokeWidth={0.8} />
        <ellipse cx={rightEndX} cy={rightEndY + 22} rx={22} ry={5.5}
          fill="none" stroke="#fff" strokeWidth={0.7} opacity="0.40" />
      </g>
      {rightLabel && (
        <g transform={`translate(${rightEndX}, ${panY(rightEndY + 18)})`}>
          <rect x="-18" y="-9" width="36" height="16" rx="8" fill={c.fill2} opacity="0.12" />
          <text x="0" y="1" textAnchor="middle" dominantBaseline="middle"
            fontSize={9} fontWeight={800} fill={c.fill2}>{rightLabel}</text>
        </g>
      )}
    </svg>
  );
}

// ─── 13. ClockVisual ─────────────────────────────────────────────────────────
// Polished: real analog clock — metallic bezel ring around white dial, three-
// tier tick hierarchy (hour/quarter/minute), tapered hour & minute hands with
// drop shadow, glossy centre cap, coral second hand for accent.
export function ClockVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const { hour = 3, minute = 0 } = params;
  const cx = size / 2, cy = size * 0.50, r = size * 0.42;
  const hourAngle = ((hour % 12) / 12) * 2 * Math.PI - Math.PI / 2 + (minute / 60) * (Math.PI / 6);
  const minAngle  = (minute / 60) * 2 * Math.PI - Math.PI / 2;
  const hx = cx + r * 0.52 * Math.cos(hourAngle), hy = cy + r * 0.52 * Math.sin(hourAngle);
  const mx = cx + r * 0.78 * Math.cos(minAngle),  my = cy + r * 0.78 * Math.sin(minAngle);
  // tail end (small back-stub for hands)
  const hxBack = cx - r * 0.10 * Math.cos(hourAngle), hyBack = cy - r * 0.10 * Math.sin(hourAngle);
  const mxBack = cx - r * 0.10 * Math.cos(minAngle),  myBack = cy - r * 0.10 * Math.sin(minAngle);
  const nums = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
      aria-label={`${hour}:${String(minute).padStart(2, '0')}`}>
      <defs>
        <RichShadow id={`${uid}_rich`} near={2} far={5} opacityNear={0.20} opacityFar={0.10} />
        {/* Metallic bezel — top brighter than bottom */}
        <linearGradient id={`${uid}_bezel`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor={lighten(c.fill1, 0.20)} />
          <stop offset="50%"  stopColor={c.fill1} />
          <stop offset="100%" stopColor={darken(c.fill1, 0.20)} />
        </linearGradient>
        {/* Dial face — subtle inner shading from edges to centre */}
        <radialGradient id={`${uid}_dial`} cx="50%" cy="42%" r="58%">
          <stop offset="0%"   stopColor={lighten(c.bg, 0.05)} />
          <stop offset="80%"  stopColor={c.bg} />
          <stop offset="100%" stopColor={darken(c.bg, 0.04)} />
        </radialGradient>
        {/* Centre cap sphere */}
        <SphereGradient id={`${uid}_cap`} base={c.fill1} cx="32%" cy="28%" />
      </defs>

      {/* Outer ground shadow */}
      <ellipse cx={cx} cy={cy + r + 4} rx={r * 0.88} ry={r * 0.10} fill="#000" opacity="0.14" />

      {/* Outer bezel ring */}
      <g filter={`url(#${uid}_rich)`}>
        <circle cx={cx} cy={cy} r={r} fill={`url(#${uid}_bezel)`} />
      </g>
      {/* Bezel highlight (top arc) */}
      <path
        d={`M ${cx - r * 0.75},${cy - r * 0.55} A ${r * 0.92},${r * 0.92} 0 0,1 ${cx + r * 0.50},${cy - r * 0.78}`}
        stroke="#fff" strokeWidth={r * 0.10} strokeLinecap="round"
        fill="none" opacity="0.40"
      />

      {/* Dial face — slightly inset */}
      <circle cx={cx} cy={cy} r={r - r * 0.13} fill={`url(#${uid}_dial)`} />
      {/* Dial inner rim shadow */}
      <circle cx={cx} cy={cy} r={r - r * 0.13} fill="none"
        stroke={darken(c.bg, 0.20)} strokeWidth={0.8} opacity="0.45" />

      {/* Tick marks: minutes (light), hours (bold) — drawn over the dial */}
      {Array.from({ length: 60 }, (_, i) => {
        const a = (i / 60) * 2 * Math.PI - Math.PI / 2;
        const isHour = i % 5 === 0;
        const r1 = r - (isHour ? r * 0.18 : r * 0.12);
        const r2 = r - r * 0.075;
        return (
          <line key={i}
            x1={cx + r1 * Math.cos(a)} y1={cy + r1 * Math.sin(a)}
            x2={cx + r2 * Math.cos(a)} y2={cy + r2 * Math.sin(a)}
            stroke={isHour ? c.text : c.muted}
            strokeWidth={isHour ? 2.2 : 0.7}
            strokeOpacity={isHour ? 0.92 : 0.55}
            strokeLinecap="round" />
        );
      })}

      {/* Hour numbers */}
      {nums.map((n, i) => {
        const a = (i / 12) * 2 * Math.PI - Math.PI / 2;
        return (
          <text key={n}
            x={cx + (r - r * 0.32) * Math.cos(a)}
            y={cy + (r - r * 0.32) * Math.sin(a) + 3.5}
            textAnchor="middle" fontSize={Math.max(9, r * 0.20)}
            fontWeight={n === 12 ? 800 : 700}
            fill={c.text}>
            {n}
          </text>
        );
      })}

      {/* Hour hand — tapered + shadow */}
      <g filter={`url(#${uid}_rich)`}>
        <line x1={hxBack} y1={hyBack} x2={hx} y2={hy}
          stroke={c.text} strokeWidth={5.5} strokeLinecap="round" />
      </g>
      {/* Minute hand */}
      <g filter={`url(#${uid}_rich)`}>
        <line x1={mxBack} y1={myBack} x2={mx} y2={my}
          stroke={c.text} strokeWidth={3.2} strokeLinecap="round" />
      </g>
      {/* Coral accent on minute hand tip */}
      <circle cx={mx} cy={my} r={2.5} fill={c.fill2} />

      {/* Centre cap — glossy sphere */}
      <g filter={`url(#${uid}_rich)`}>
        <circle cx={cx} cy={cy} r={6} fill={c.fill1} />
        <circle cx={cx} cy={cy} r={6} fill={`url(#${uid}_cap)`} />
      </g>
      <ellipse cx={cx - 2} cy={cy - 2} rx="1.8" ry="1" fill="#fff" opacity="0.70" />
      {/* Centre pin */}
      <circle cx={cx} cy={cy} r={1.4} fill={c.fill1} />
    </svg>
  );
}

// ─── 14. BarChartVisual ──────────────────────────────────────────────────────
// Polished: each bar wears a vertical gradient (lighter top → fuller bottom),
// rests on a soft floor shadow, and carries a thin specular stripe down the
// left edge to mimic a moulded plastic block. Y-axis gridlines + axis stroke
// give the chart a "data viz" finish.
export function BarChartVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const { values = [3, 5, 2, 4], labels = [] } = params;
  const maxV = Math.max(...values, 1);
  const padL = 24, padB = 26, padT = 14, padR = 10;
  const chartW = size - padL - padR;
  const chartH = size - padB - padT;
  const barW = chartW / values.length - 6;
  const baseColors = [c.fill1, c.fill2, c.fill3, c.fill4, c.fill5, c.fill6];

  // Unique gradient def per palette colour
  const gradDefs = baseColors.map((col, i) => (
    <linearGradient key={i} id={`${uid}_g${i}`} x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%"   stopColor={lighten(col, 0.22)} />
      <stop offset="100%" stopColor={col} />
    </linearGradient>
  ));

  // Y-axis gridlines
  const gridLines = [0.25, 0.5, 0.75, 1.0].map(f => {
    const y = padT + chartH * (1 - f);
    return (
      <line key={f} x1={padL} y1={y} x2={size - padR} y2={y}
        stroke={c.line} strokeWidth={0.5} strokeDasharray="4,3" opacity={0.55} />
    );
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="bar chart">
      <defs>
        <RichShadow id={`${uid}_rich`} near={0.8} far={2.2} opacityNear={0.18} opacityFar={0.08} />
        {gradDefs}
      </defs>

      {/* Grid + axes */}
      {gridLines}
      <line x1={padL} y1={padT} x2={padL} y2={size - padB} stroke={c.muted} strokeWidth={1.6} strokeLinecap="round" />
      <line x1={padL} y1={size - padB} x2={size - padR} y2={size - padB} stroke={c.muted} strokeWidth={1.6} strokeLinecap="round" />

      {/* Bars */}
      {values.map((v, i) => {
        const barH = (v / maxV) * chartH;
        const x = padL + 4 + i * (barW + 6);
        const y = padT + chartH - barH;
        const gradId = `${uid}_g${i % baseColors.length}`;
        const col = baseColors[i % baseColors.length];
        return (
          <g key={i}>
            {/* Bar body with gradient + rich shadow */}
            <g filter={`url(#${uid}_rich)`}>
              <rect x={x} y={y} width={barW} height={barH}
                fill={`url(#${gradId})`} rx={4} stroke={darken(col, 0.12)}
                strokeWidth={0.5} strokeOpacity={0.40} />
            </g>
            {/* Left specular stripe — sells the moulded plastic look */}
            <rect x={x + 2} y={y + 3} width={2} height={Math.max(0, barH - 6)}
              fill="#fff" opacity="0.30" rx={1} />
            {/* Value badge on top */}
            <text x={x + barW / 2} y={y - 5} textAnchor="middle"
              fontSize={9.5} fontWeight={800} fill={col}>{v}</text>
            {/* Category label */}
            {labels[i] && (
              <text x={x + barW / 2} y={Math.min(size - padB + 14, size - 4)}
                textAnchor="middle" fontSize={7.5} fontWeight={600} fill={c.dim}>
                {labels[i].length > 6 ? labels[i].slice(0, 5) + '…' : labels[i]}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ─── 15. PieChartVisual ──────────────────────────────────────────────────────
// Polished: each slice has its own radial gradient (centre brighter, edge
// darker) for a glossy disc finish; white slice separators run from centre
// to rim; a specular arc on top-left + ground shadow ellipse give the pie a
// real physical disc presence. Legend pills sit underneath when space allows.
export function PieChartVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const { slices = [30, 45, 25] } = params;
  const cx = size * 0.50, cy = size * 0.44, r = size * 0.34;
  const total = slices.reduce((a, b) => a + b, 0);
  const baseColors = [c.fill1, c.fill2, c.fill3, c.fill4, c.fill5, c.fill6];

  // Build slice paths + per-slice gradient defs
  let startAngle = -Math.PI / 2;
  const sliceData = slices.map((s, i) => {
    const angle = (s / total) * 2 * Math.PI;
    const x1 = cx + r * Math.cos(startAngle), y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(startAngle + angle), y2 = cy + r * Math.sin(startAngle + angle);
    const large = angle > Math.PI ? 1 : 0;
    const midA = startAngle + angle / 2;
    const labelR = r * 0.62;
    const lx = cx + labelR * Math.cos(midA), ly = cy + labelR * Math.sin(midA);
    const startA = startAngle;
    startAngle += angle;
    const col = baseColors[i % baseColors.length];
    return {
      d: `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large},1 ${x2},${y2} Z`,
      col, pct: Math.round(s / total * 100),
      lx, ly, midA, startA,
    };
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="pie chart">
      <defs>
        <RichShadow id={`${uid}_rich`} near={1.5} far={4} />
        {sliceData.map((sl, i) => (
          <radialGradient key={i} id={`${uid}_s${i}`} cx="40%" cy="36%" r="70%">
            <stop offset="0%"   stopColor={lighten(sl.col, 0.25)} />
            <stop offset="60%"  stopColor={sl.col} />
            <stop offset="100%" stopColor={darken(sl.col, 0.12)} />
          </radialGradient>
        ))}
      </defs>

      {/* Ground shadow under disc */}
      <ellipse cx={cx} cy={cy + r + 4} rx={r * 0.85} ry={r * 0.10}
        fill="#000" opacity="0.12" />

      {/* Slices with rich shadow */}
      <g filter={`url(#${uid}_rich)`}>
        {sliceData.map((sl, i) => (
          <path key={i} d={sl.d} fill={`url(#${uid}_s${i})`} />
        ))}
      </g>

      {/* Glossy specular arc on top-left of the disc */}
      <path
        d={`M ${cx - r * 0.55},${cy - r * 0.65} A ${r * 0.85},${r * 0.85} 0 0,1 ${cx + r * 0.20},${cy - r * 0.85}`}
        stroke="#fff" strokeWidth={r * 0.16} strokeLinecap="round"
        fill="none" opacity="0.30"
      />

      {/* White slice separators */}
      {sliceData.map((sl, i) => {
        const x = cx + r * Math.cos(sl.startA), y = cy + r * Math.sin(sl.startA);
        return (
          <line key={`sep${i}`} x1={cx} y1={cy} x2={x} y2={y}
            stroke="#fff" strokeWidth={1.6} opacity="0.90" />
        );
      })}

      {/* Centre cap */}
      <circle cx={cx} cy={cy} r={3.5} fill={c.white}
        stroke={c.muted} strokeOpacity="0.35" strokeWidth={0.5} />

      {/* Percentage labels inside slices, only when slice large enough */}
      {sliceData.map((sl, i) => sl.pct >= 10 && (
        <text key={`l${i}`} x={sl.lx} y={sl.ly + 3} textAnchor="middle"
          fontSize={10} fontWeight={800} fill="#fff"
          style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.30))' }}>
          {sl.pct}%
        </text>
      ))}
    </svg>
  );
}

// ─── 16. TableVisual ─────────────────────────────────────────────────────────
// Polished: header row wears a logo-teal gradient with a subtle highlight
// stripe (like a book heading), body cells alternate between surface and
// soft1 tint (zebra stripe). Outer container has rounded corners + drop
// shadow so the table looks like a printed card.
export function TableVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const { rows = 3, cols = 3, data = [] } = params;
  // No data in the concept: draw a small tally — a shape per row, counts beside it.
  const isSample = data.length === 0;
  const SAMPLE_COUNTS = [[3, 5], [4, 2], [6, 1], [2, 4], [5, 3]];
  const sampleColors = [c.fill1, c.fill2, c.fill3, c.fill6, c.fill5];
  const padX = 12, padY = size * 0.10;
  const tableW = size - padX * 2;
  const cellH = (size - padY - 12) / (rows + 1);
  const cellW = tableW / cols;
  const startX = padX;
  const startY = padY;
  const radius = 6;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="tablo">
      <defs>
        <RichShadow id={`${uid}_rich`} near={1.5} far={3.5} />
        <linearGradient id={`${uid}_head`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor={lighten(c.fill1, 0.18)} />
          <stop offset="100%" stopColor={c.fill1} />
        </linearGradient>
        <linearGradient id={`${uid}_body`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor={lighten(c.bg, 0.05)} />
          <stop offset="100%" stopColor={c.bg} />
        </linearGradient>
        {/* Clip path to round table corners */}
        <clipPath id={`${uid}_clip`}>
          <rect x={startX} y={startY} width={tableW} height={cellH * (rows + 1)} rx={radius} />
        </clipPath>
      </defs>

      {/* Drop-shadowed body */}
      <g filter={`url(#${uid}_rich)`}>
        <rect x={startX} y={startY} width={tableW} height={cellH * (rows + 1)}
          fill={`url(#${uid}_body)`} rx={radius} />
      </g>

      {/* Cells (clipped to rounded outline) */}
      <g clipPath={`url(#${uid}_clip)`}>
        {/* Header row */}
        <rect x={startX} y={startY} width={tableW} height={cellH}
          fill={`url(#${uid}_head)`} />
        {/* Header inner highlight */}
        <rect x={startX + 1} y={startY + 1} width={tableW - 2} height={cellH * 0.35}
          fill="#fff" opacity="0.22" />

        {/* Zebra stripe body rows */}
        {Array.from({ length: rows }, (_, r) => (
          r % 2 === 0 ? null : (
            <rect key={`zb${r}`}
              x={startX} y={startY + (r + 1) * cellH}
              width={tableW} height={cellH}
              fill={c.soft1} opacity="0.55" />
          )
        ))}
      </g>

      {/* Container border (subtle) */}
      <rect x={startX} y={startY} width={tableW} height={cellH * (rows + 1)}
        fill="none" stroke={c.line} strokeWidth={0.6} rx={radius} opacity="0.5" />

      {/* Grid lines (horizontal between rows) */}
      {Array.from({ length: rows }, (_, i) => (
        <line key={`hl${i}`}
          x1={startX} y1={startY + (i + 1) * cellH}
          x2={startX + tableW} y2={startY + (i + 1) * cellH}
          stroke={c.line} strokeWidth={0.5} opacity="0.40" />
      ))}
      {/* Grid lines (vertical between cols) */}
      {Array.from({ length: cols - 1 }, (_, i) => (
        <line key={`vl${i}`}
          x1={startX + (i + 1) * cellW} y1={startY + cellH * 0.55}
          x2={startX + (i + 1) * cellW} y2={startY + cellH * (rows + 1)}
          stroke={c.line} strokeWidth={0.5} opacity="0.30" />
      ))}

      {/* Cell text */}
      {Array.from({ length: rows + 1 }, (_, r) => (
        Array.from({ length: cols }, (_, col) => {
          const isHeader = r === 0;
          const dataR = r - 1;
          const x = startX + col * cellW + cellW / 2;
          const y = startY + r * cellH + cellH / 2 + 3;
          if (isSample) {
            if (isHeader) {
              // Placeholder bar instead of a made-up column title
              return (
                <rect key={`t${r}${col}`} x={x - cellW * 0.22} y={y - 6} width={cellW * 0.44} height={5}
                  rx={2.5} fill="#fff" opacity="0.75" />
              );
            }
            if (col === 0) {
              const shapeR = Math.min(cellH, cellW) * 0.24;
              const fill = sampleColors[dataR % sampleColors.length];
              return dataR % 3 === 1
                ? <rect key={`t${r}${col}`} x={x - shapeR} y={y - 3 - shapeR} width={shapeR * 2} height={shapeR * 2} rx={shapeR * 0.3} fill={fill} />
                : dataR % 3 === 2
                  ? <polygon key={`t${r}${col}`} points={`${x},${y - 3 - shapeR} ${x + shapeR},${y - 3 + shapeR} ${x - shapeR},${y - 3 + shapeR}`} fill={fill} />
                  : <circle key={`t${r}${col}`} cx={x} cy={y - 3} r={shapeR} fill={fill} />;
            }
          }
          const text = isSample
            ? String(SAMPLE_COUNTS[dataR % SAMPLE_COUNTS.length][(col - 1) % 2])
            : isHeader
              ? (data[-1] && data[-1][col]) || ''
              : (data[dataR] && data[dataR][col]) || '';
          if (!text) return null;
          return (
            <text key={`t${r}${col}`} x={x} y={y} textAnchor="middle"
              fontSize={isHeader ? 9 : isSample ? Math.min(cellH * 0.5, 15) : 8.5}
              fontWeight={isHeader || isSample ? 800 : 600}
              fill={isHeader ? '#fff' : c.text}
              letterSpacing={isHeader ? '0.04em' : '0'}>
              {text}
            </text>
          );
        })
      ))}
    </svg>
  );
}

// ─── 17. DiceVisual ──────────────────────────────────────────────────────────
// Polished: 3D isometric die with three visible faces (top, left, right). The
// front face carries the active pip count rendered as recessed (inset) dots
// using a glossy gradient pip + a subtle ring shadow so they look pressed
// into the surface. Side faces wear darker shading for a "lit-from-above"
// look that matches the rest of the visual library.
export function DiceVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const { face = 3 } = params;
  // Front face is square; size is the side length
  const s = size * 0.50;
  const bx = (size - s) / 2;
  const by = (size - s) / 2 + size * 0.04;
  const dotR = s * 0.10;
  const depth = s * 0.18; // isometric depth offset
  const dotPositions = {
    1: [[0.5, 0.5]],
    2: [[0.28, 0.28], [0.72, 0.72]],
    3: [[0.28, 0.28], [0.5, 0.5], [0.72, 0.72]],
    4: [[0.28, 0.28], [0.72, 0.28], [0.28, 0.72], [0.72, 0.72]],
    5: [[0.28, 0.28], [0.72, 0.28], [0.5, 0.5], [0.28, 0.72], [0.72, 0.72]],
    6: [[0.28, 0.22], [0.72, 0.22], [0.28, 0.5], [0.72, 0.5], [0.28, 0.78], [0.72, 0.78]],
  };

  // Die face background — bone white with a hint of warm tint
  const faceFill = lighten(c.bg, 0.10);
  const sideMid  = darken(faceFill, 0.10);
  const sideDark = darken(faceFill, 0.22);
  const pipColor = c.text;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`zar ${face}`}>
      <defs>
        <RichShadow id={`${uid}_rich`} near={2} far={5} opacityNear={0.22} opacityFar={0.12} />
        <linearGradient id={`${uid}_top`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor={lighten(faceFill, 0.12)} />
          <stop offset="100%" stopColor={faceFill} />
        </linearGradient>
        <linearGradient id={`${uid}_front`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor={faceFill} />
          <stop offset="100%" stopColor={darken(faceFill, 0.06)} />
        </linearGradient>
        <linearGradient id={`${uid}_side`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor={sideMid} />
          <stop offset="100%" stopColor={sideDark} />
        </linearGradient>
        <radialGradient id={`${uid}_pip`} cx="35%" cy="30%" r="65%">
          <stop offset="0%"   stopColor={lighten(pipColor, 0.50)} />
          <stop offset="50%"  stopColor={pipColor} />
          <stop offset="100%" stopColor={darken(pipColor, 0.15)} />
        </radialGradient>
      </defs>

      {/* Ground shadow */}
      <ellipse cx={size / 2} cy={by + s + depth + 8} rx={s * 0.55} ry={s * 0.10}
        fill="#000" opacity="0.18" />

      <g filter={`url(#${uid}_rich)`}>
        {/* Right face (between front and top) */}
        <polygon points={
          `${bx + s},${by} ${bx + s + depth},${by - depth} ` +
          `${bx + s + depth},${by + s - depth} ${bx + s},${by + s}`
        } fill={`url(#${uid}_side)`} stroke={darken(faceFill, 0.30)}
          strokeWidth={0.6} strokeLinejoin="round" />
        {/* Top face */}
        <polygon points={
          `${bx},${by} ${bx + depth},${by - depth} ` +
          `${bx + s + depth},${by - depth} ${bx + s},${by}`
        } fill={`url(#${uid}_top)`} stroke={darken(faceFill, 0.20)}
          strokeWidth={0.6} strokeLinejoin="round" />
        {/* Front face — rounded for friendly look */}
        <rect x={bx} y={by} width={s} height={s} rx={s * 0.14}
          fill={`url(#${uid}_front)`} stroke={darken(faceFill, 0.15)} strokeWidth={0.6} />
      </g>

      {/* Top-edge specular */}
      <rect x={bx + s * 0.10} y={by + 3} width={s * 0.55} height={s * 0.06}
        fill="#fff" opacity="0.40" rx={2} />

      {/* Pips — recessed sphere look */}
      {(dotPositions[face] || dotPositions[1]).map(([dx, dy], i) => {
        const cx = bx + s * dx, cy = by + s * dy;
        return (
          <g key={i}>
            {/* Recessed shadow underneath the pip */}
            <circle cx={cx + 0.5} cy={cy + 1} r={dotR + 0.5}
              fill={darken(faceFill, 0.25)} opacity="0.30" />
            {/* Pip body with gradient */}
            <circle cx={cx} cy={cy} r={dotR} fill={pipColor} />
            <circle cx={cx} cy={cy} r={dotR} fill={`url(#${uid}_pip)`} />
            {/* Tiny specular highlight */}
            <ellipse cx={cx - dotR * 0.30} cy={cy - dotR * 0.35}
              rx={dotR * 0.30} ry={dotR * 0.18}
              fill="#fff" opacity="0.55" />
          </g>
        );
      })}
    </svg>
  );
}

// ─── 18. ProbabilityVisual ───────────────────────────────────────────────────
// Polished: in "coin" mode two glossy coins (heads/tails) sit side-by-side
// with a halved equation badge below; in slider mode a multi-stop gradient
// fills a smoothed track with a glowing sphere thumb (data-bar feel).
export function ProbabilityVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const { value = 0.5, coin = false } = params;

  if (coin) {
    const r = size * 0.24;
    // Coin sphere — gradient body + ring rim + specular
    const coinG = (cx, cy, base, label) => (
      <g>
        <ellipse cx={cx} cy={cy + r * 0.85} rx={r * 0.75} ry={r * 0.12}
          fill="#000" opacity="0.16" />
        <g filter={`url(#${uid}_rich)`}>
          <circle cx={cx} cy={cy} r={r} fill={base} />
          <circle cx={cx} cy={cy} r={r} fill={`url(#${uid}_${label}G)`} />
        </g>
        {/* Outer ring rim */}
        <circle cx={cx} cy={cy} r={r - 1.5} fill="none"
          stroke={darken(base, 0.20)} strokeWidth={0.8} strokeOpacity="0.50" />
        {/* Specular highlight */}
        <ellipse cx={cx - r * 0.32} cy={cy - r * 0.38} rx={r * 0.32} ry={r * 0.20}
          fill="#fff" opacity="0.60" />
        {/* Letter */}
        <text x={cx} y={cy + 5} textAnchor="middle"
          fontSize={16} fontWeight={900} fill="#fff"
          style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.30))' }}>
          {label}
        </text>
      </g>
    );

    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="perçe">
        <defs>
          <RichShadow id={`${uid}_rich`} near={1.5} far={4} />
          <radialGradient id={`${uid}_TG`} cx="32%" cy="28%" r="68%">
            <stop offset="0%"   stopColor={lighten(c.fill4, 0.40)} />
            <stop offset="55%"  stopColor={c.fill4} />
            <stop offset="100%" stopColor={darken(c.fill4, 0.12)} />
          </radialGradient>
          <radialGradient id={`${uid}_YG`} cx="32%" cy="28%" r="68%">
            <stop offset="0%"   stopColor={lighten(c.fill1, 0.40)} />
            <stop offset="55%"  stopColor={c.fill1} />
            <stop offset="100%" stopColor={darken(c.fill1, 0.12)} />
          </radialGradient>
        </defs>
        {coinG(size * 0.30, size * 0.42, c.fill4, 'T')}
        {coinG(size * 0.70, size * 0.42, c.fill1, 'Y')}
        {/* Equation badge */}
        <g transform={`translate(${size / 2}, ${size * 0.84})`}>
          <rect x={-40} y={-12} width={80} height={24} rx={12}
            fill={c.fill1} opacity="0.10" />
          <text x={0} y={1} textAnchor="middle" dominantBaseline="middle"
            fontSize={12} fontWeight={800} fill={c.fill1}
            fontFamily="'Inter',system-ui">½ + ½ = 1</text>
        </g>
      </svg>
    );
  }

  // ── Probability slider ──
  const trackX = 18, trackW = size - 36, trackH = 14, trackY = size * 0.46;
  const fillW = Math.max(0, Math.min(trackW, trackW * value));
  const labels = ['0', '¼', '½', '¾', '1'];
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
      aria-label={`îhtîmal ${value}`}>
      <defs>
        <RichShadow id={`${uid}_rich`} near={1} far={3} />
        <linearGradient id={`${uid}_pg`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor={c.fill5} />
          <stop offset="50%"  stopColor={c.fill4} />
          <stop offset="100%" stopColor={c.fill3} />
        </linearGradient>
        <linearGradient id={`${uid}_track`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor={darken(c.line, 0.05)} />
          <stop offset="100%" stopColor={lighten(c.line, 0.05)} />
        </linearGradient>
        <SphereGradient id={`${uid}_thumb`} base={c.fill3} cx="32%" cy="28%" />
        <clipPath id={`${uid}_clip`}>
          <rect x={trackX} y={trackY} width={fillW} height={trackH} rx={7} />
        </clipPath>
      </defs>

      {/* End labels */}
      <g transform={`translate(${trackX + 18}, ${trackY - 12})`}>
        <rect x={-18} y={-9} width={36} height={16} rx={8} fill={c.fill5} opacity="0.14" />
        <text x={0} y={1} textAnchor="middle" dominantBaseline="middle"
          fontSize={9} fontWeight={800} fill={c.fill5}>Negengaz</text>
      </g>
      <g transform={`translate(${trackX + trackW - 18}, ${trackY - 12})`}>
        <rect x={-15} y={-9} width={30} height={16} rx={8} fill={c.fill3} opacity="0.14" />
        <text x={0} y={1} textAnchor="middle" dominantBaseline="middle"
          fontSize={9} fontWeight={800} fill={c.fill3}>Teqez</text>
      </g>

      {/* Track (inset look with subtle inner shadow line) */}
      <rect x={trackX} y={trackY} width={trackW} height={trackH} rx={7}
        fill={`url(#${uid}_track)`} />
      <rect x={trackX} y={trackY} width={trackW} height={2} rx={1}
        fill="#000" opacity="0.10" />
      {/* Fill */}
      <rect x={trackX} y={trackY} width={trackW} height={trackH} rx={7}
        fill={`url(#${uid}_pg)`} clipPath={`url(#${uid}_clip)`} />

      {/* Glossy thumb sphere */}
      <g filter={`url(#${uid}_rich)`}>
        <circle cx={trackX + fillW} cy={trackY + trackH / 2} r={11} fill={c.fill3} />
        <circle cx={trackX + fillW} cy={trackY + trackH / 2} r={11} fill={`url(#${uid}_thumb)`} />
      </g>
      <ellipse cx={trackX + fillW - 3.5} cy={trackY + trackH / 2 - 4} rx={3.5} ry={2}
        fill="#fff" opacity="0.60" />
      <text x={trackX + fillW} y={trackY + trackH / 2 + 4} textAnchor="middle"
        fontSize={8} fontWeight={900} fill="#fff"
        style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.30))' }}>
        {value}
      </text>

      {/* Tick labels under the track */}
      {labels.map((l, i) => (
        <text key={i} x={trackX + (i / 4) * trackW} y={trackY + trackH + 20}
          textAnchor="middle" fontSize={9} fontWeight={700} fill={c.muted}>{l}</text>
      ))}
    </svg>
  );
}

// ─── 19. AlgebraVisual (pure SVG) ────────────────────────────────────────────
// Polished: equation mode renders a compact balance beneath the equation
// pill; balanced state uses logo green, unbalanced uses logo coral. Variable
// mode shows a dashed-bordered "mystery box" with a glowing ? inside and a
// soft inner gradient (the "what goes here?" feel).
export function AlgebraVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const { type: vtype = 'variable', equation = '□ + 3 = 7', balanced = true } = params;

  if (vtype === 'equation') {
    const cx = size / 2, baseY = size * 0.74, postH = size * 0.34;
    const tilt = balanced ? 0 : 9;
    const beamColor = balanced ? c.fill3 : c.fill2;
    const fontSize = equation.length > 14 ? 9 : equation.length > 10 ? 11 : 13;
    const sides = equation.includes('=') ? equation.split('=').map(part => part.trim()) : null;
    const sideSize = side => (side.length > 6 ? 10 : side.length > 3 ? 12 : 15);
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
        aria-label={equation}>
        <defs>
          <RichShadow id={`${uid}_rich`} near={1.2} far={3} />
          <linearGradient id={`${uid}_post`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   stopColor={lighten(c.muted, 0.18)} />
            <stop offset="100%" stopColor={darken(c.muted, 0.18)} />
          </linearGradient>
          <linearGradient id={`${uid}_beam`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   stopColor={lighten(beamColor, 0.20)} />
            <stop offset="100%" stopColor={darken(beamColor, 0.10)} />
          </linearGradient>
          <SphereGradient id={`${uid}_pivot`} base={beamColor} cx="32%" cy="28%" />
        </defs>

        {/* Equation pill at the top */}
        <g transform={`translate(${size / 2}, ${size * 0.16})`}>
          <rect x={-Math.min(size * 0.40, equation.length * fontSize * 0.32)} y={-13}
            width={Math.min(size * 0.80, equation.length * fontSize * 0.64)} height={26} rx={13}
            fill={c.fill1} opacity="0.12" />
          <text x={0} y={1} textAnchor="middle" dominantBaseline="middle"
            fontSize={fontSize} fontWeight={900} fill={c.fill1}
            fontFamily="'Inter',system-ui,monospace">{equation}</text>
        </g>

        {/* Ground shadow */}
        <ellipse cx={cx} cy={baseY + 11} rx={size * 0.20} ry={3} fill="#000" opacity="0.14" />

        {/* Base plate */}
        <rect x={cx - 24} y={baseY + 4} width={48} height={3.5}
          fill={darken(c.muted, 0.20)} rx={1.5} />
        <rect x={cx - 24} y={baseY} width={48} height={6} rx={3}
          fill={`url(#${uid}_post)`} />

        {/* Vertical post */}
        <rect x={cx - 2.5} y={baseY - postH} width={5} height={postH}
          fill={`url(#${uid}_post)`} rx={1.5} />

        {/* Beam */}
        <line x1={cx - size * 0.28} y1={baseY - postH + tilt}
          x2={cx + size * 0.28} y2={baseY - postH - tilt}
          stroke={`url(#${uid}_beam)`} strokeWidth={3.5} strokeLinecap="round" />

        {/* Pivot — glossy sphere */}
        <g filter={`url(#${uid}_rich)`}>
          <circle cx={cx} cy={baseY - postH} r={5} fill={beamColor} />
          <circle cx={cx} cy={baseY - postH} r={5} fill={`url(#${uid}_pivot)`} />
        </g>

        {/* Pans (open ellipses) */}
        <ellipse cx={cx - size * 0.28} cy={baseY - postH + tilt + 16}
          rx={20} ry={5.5} fill="none" stroke={beamColor} strokeWidth={2} />
        <ellipse cx={cx + size * 0.28} cy={baseY - postH - tilt + 16}
          rx={20} ry={5.5} fill="none" stroke={beamColor} strokeWidth={2} />

        {/* Each side of the equation rests on its pan */}
        {sides && sides.length === 2 && sides.map((side, i) => (
          <text key={i}
            x={cx + (i === 0 ? -1 : 1) * size * 0.28}
            y={baseY - postH + (i === 0 ? tilt : -tilt) + 7}
            textAnchor="middle" fontSize={sideSize(side)} fontWeight={900} fill={c.text}
            fontFamily="'Inter',system-ui,monospace">{side}</text>
        ))}
      </svg>
    );
  }

  // Variable mystery-box
  const bx = size * 0.28, by = size * 0.18, bs = size * 0.44;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="guherbar">
      <defs>
        <RichShadow id={`${uid}_rich`} near={1.5} far={3.5} />
        <radialGradient id={`${uid}_var`} cx="40%" cy="36%" r="80%">
          <stop offset="0%"   stopColor={lighten(c.fill1, 0.65)} />
          <stop offset="100%" stopColor={c.soft1} />
        </radialGradient>
      </defs>
      <g filter={`url(#${uid}_rich)`}>
        <rect x={bx} y={by} width={bs} height={bs} rx={12}
          fill={`url(#${uid}_var)`} stroke={c.fill1} strokeWidth={2.5}
          strokeDasharray="7,4" />
      </g>
      {/* Question mark with a soft inner glow */}
      <circle cx={size / 2} cy={by + bs / 2} r={bs * 0.30}
        fill={c.fill1} opacity="0.10" />
      <text x={size / 2} y={by + bs / 2 + 2} textAnchor="middle"
        dominantBaseline="middle"
        fontSize={32} fontWeight={900} fill={c.fill1}
        style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.18))' }}>?</text>
      <g transform={`translate(${size / 2}, ${by + bs + 20})`}>
        <rect x={-32} y={-9} width={64} height={18} rx={9} fill={c.fill1} opacity="0.12" />
        <text x={0} y={1} textAnchor="middle" dominantBaseline="middle"
          fontSize={10} fontWeight={800} fill={c.fill1}>guherbar</text>
      </g>
    </svg>
  );
}

// ─── 20. PatternVisual ───────────────────────────────────────────────────────
// Polished: each pattern element is a glossy sphere/square/triangle. The
// unknown ("?") slot wears a dashed pill with a pulsing tint so it reads as
// the question. A subtle row baseline anchors them on the same "shelf".
export function PatternVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const { items = ['🔵', '🟡', '🔵', '🟡', '?'], count = 5 } = params;
  const shapeMap = { '🔵':'circle','🟡':'square','🔺':'triangle','🟢':'circle','🔴':'circle','🟠':'circle' };
  const colorMap = { '🔵':c.fill1,'🟡':c.fill4,'🔺':c.fill5,'🟢':c.fill3,'🔴':c.fill5,'🟠':c.fill2 };
  const n = Math.min(count, 7);
  const spacing = (size - 20) / n;
  const r = Math.min(spacing * 0.36, 20);
  const midY = size * 0.42;

  // Collect unique gradient defs so each element has its own highlight
  const gradDefs = [];
  const elems = [];
  for (let i = 0; i < n; i++) {
    const cx = 12 + i * spacing + spacing / 2;
    const item = items[i] || (i % 2 === 0 ? '🔵' : '🟡');
    const isLast = item === '?' || (i === n - 1 && items[n - 1] === '?');
    if (isLast) {
      elems.push(
        <g key={`box${i}`}>
          <rect x={cx - r} y={midY - r} width={r * 2} height={r * 2} rx={6}
            fill={c.soft1} stroke={c.muted} strokeWidth={1.8} strokeDasharray="4,2.5" />
          <text x={cx} y={midY + 4} textAnchor="middle" dominantBaseline="middle"
            fontSize={18} fontWeight={900} fill={c.muted}>?</text>
        </g>
      );
    } else {
      const shape = shapeMap[item] || 'circle';
      const col   = colorMap[item] || c.fill1;
      const gid   = `${uid}_g${i}`;
      gradDefs.push(<SphereGradient key={gid} id={gid} base={col} cx="32%" cy="28%" />);
      if (shape === 'triangle') {
        elems.push(
          <g key={i} filter={`url(#${uid}_rich)`}>
            <polygon points={`${cx},${midY - r} ${cx - r},${midY + r} ${cx + r},${midY + r}`}
              fill={col} strokeLinejoin="round" />
            <polygon points={`${cx},${midY - r} ${cx - r},${midY + r} ${cx + r},${midY + r}`}
              fill={`url(#${gid})`} strokeLinejoin="round" />
          </g>
        );
      } else if (shape === 'square') {
        elems.push(
          <g key={i} filter={`url(#${uid}_rich)`}>
            <rect x={cx - r} y={midY - r} width={r * 2} height={r * 2} rx={r * 0.18}
              fill={col} />
            <rect x={cx - r} y={midY - r} width={r * 2} height={r * 2} rx={r * 0.18}
              fill={`url(#${gid})`} />
            <rect x={cx - r + 2} y={midY - r + 2} width={r * 2 - 4} height={r * 0.5} rx={r * 0.13}
              fill="#fff" opacity="0.22" />
          </g>
        );
      } else {
        elems.push(
          <g key={i} filter={`url(#${uid}_rich)`}>
            <circle cx={cx} cy={midY} r={r} fill={col} />
            <circle cx={cx} cy={midY} r={r} fill={`url(#${gid})`} />
            <ellipse cx={cx - r * 0.32} cy={midY - r * 0.40} rx={r * 0.32} ry={r * 0.20}
              fill="#fff" opacity="0.55" />
          </g>
        );
      }
    }
  }

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="şêwe">
      <defs>
        <RichShadow id={`${uid}_rich`} near={0.8} far={2} opacityNear={0.20} opacityFar={0.10} />
        {gradDefs}
      </defs>
      {/* Subtle baseline shelf */}
      <line x1={10} y1={midY + r + 4} x2={size - 10} y2={midY + r + 4}
        stroke={c.line} strokeWidth={0.5} opacity="0.40" />
      {elems}
      {size >= 120 && (
        <g transform={`translate(${size / 2}, ${size * 0.78})`}>
          <rect x={-66} y={-9} width={132} height={18} rx={9} fill={c.fill2} opacity="0.10" />
          <text x={0} y={1} textAnchor="middle" dominantBaseline="middle"
            fontSize={9} fontWeight={700} fill={c.fill2}>
            Şêwe berdewam bike…
          </text>
        </g>
      )}
    </svg>
  );
}

// ─── 21. SetVisual ───────────────────────────────────────────────────────────
// Polished: two overlapping Venn circles wear radial gradient fills (lighter
// centre → tinted edge) so each set feels like a glowing disc; the
// intersection is rendered as a single warmer wedge using a clip-path so it
// reads as the "shared" region. Set labels sit in tinted pills.
export function SetVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const { labels = ['A', 'B'] } = params;
  const cx = size / 2, cy = size * 0.56, r = size * 0.30;
  const offset = r * 0.62;
  const labelY = cy - r - size * 0.09;
  const aCx = cx - offset * 0.7;
  const bCx = cx + offset * 0.7;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="kom">
      <defs>
        <RichShadow id={`${uid}_rich`} near={1.5} far={3.5} />
        <radialGradient id={`${uid}_setA`} cx="50%" cy="40%" r="70%">
          <stop offset="0%"   stopColor={lighten(c.fill1, 0.30)} stopOpacity="0.45" />
          <stop offset="100%" stopColor={c.fill1} stopOpacity="0.30" />
        </radialGradient>
        <radialGradient id={`${uid}_setB`} cx="50%" cy="40%" r="70%">
          <stop offset="0%"   stopColor={lighten(c.fill2, 0.30)} stopOpacity="0.45" />
          <stop offset="100%" stopColor={c.fill2} stopOpacity="0.30" />
        </radialGradient>
        <radialGradient id={`${uid}_intersect`} cx="50%" cy="40%" r="70%">
          <stop offset="0%"   stopColor={lighten(c.fill4, 0.40)} stopOpacity="0.85" />
          <stop offset="100%" stopColor={c.fill4} stopOpacity="0.60" />
        </radialGradient>
        <clipPath id={`${uid}_clipA`}>
          <circle cx={aCx} cy={cy} r={r} />
        </clipPath>
      </defs>

      {/* Set A — gradient fill + thick stroke */}
      <g filter={`url(#${uid}_rich)`}>
        <circle cx={aCx} cy={cy} r={r}
          fill={`url(#${uid}_setA)`} stroke={c.fill1} strokeWidth={2.5} strokeOpacity="0.65" />
      </g>
      {/* Set A specular highlight */}
      <ellipse cx={aCx - r * 0.35} cy={cy - r * 0.50} rx={r * 0.32} ry={r * 0.16}
        fill="#fff" opacity="0.40" transform={`rotate(-25 ${aCx - r * 0.35} ${cy - r * 0.50})`} />

      {/* Set B */}
      <g filter={`url(#${uid}_rich)`}>
        <circle cx={bCx} cy={cy} r={r}
          fill={`url(#${uid}_setB)`} stroke={c.fill2} strokeWidth={2.5} strokeOpacity="0.65" />
      </g>
      <ellipse cx={bCx - r * 0.35} cy={cy - r * 0.50} rx={r * 0.32} ry={r * 0.16}
        fill="#fff" opacity="0.40" transform={`rotate(-25 ${bCx - r * 0.35} ${cy - r * 0.50})`} />

      {/* Intersection — B clipped by A's circle so only overlap is filled */}
      <circle cx={bCx} cy={cy} r={r}
        fill={`url(#${uid}_intersect)`} clipPath={`url(#${uid}_clipA)`} />

      {/* Labels — tinted pills */}
      <g transform={`translate(${aCx - r * 0.45}, ${labelY})`}>
        <rect x={-14} y={-12} width={28} height={24} rx={12} fill={c.fill1} opacity="0.16" />
        <text x={0} y={1} textAnchor="middle" dominantBaseline="middle"
          fontSize={16} fontWeight={900} fill={c.fill1}>{labels[0]}</text>
      </g>
      <g transform={`translate(${bCx + r * 0.45}, ${labelY})`}>
        <rect x={-14} y={-12} width={28} height={24} rx={12} fill={c.fill2} opacity="0.16" />
        <text x={0} y={1} textAnchor="middle" dominantBaseline="middle"
          fontSize={16} fontWeight={900} fill={c.fill2}>{labels[1] || 'B'}</text>
      </g>
      {/* ∩ symbol in intersection centre */}
      <text x={cx} y={cy + 5} textAnchor="middle" dominantBaseline="middle"
        fontSize={14} fontWeight={900} fill="#fff"
        style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.35))' }}>∩</text>
    </svg>
  );
}

// ─── 22. NumberGridVisual (NEW) ──────────────────────────────────────────────
// Polished: each cell is a subtle paper tile; highlighted numbers wear a
// gradient body with a glossy specular highlight (selected button feel),
// crossed-out numbers get an X-stroke through them with dimmed text.
export function NumberGridVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const { cols: rawCols = 5, rows: rawRows = 4, highlight = [], crossed = [], startAt = 1 } = params;
  const cols = Math.min(rawCols, 10), rows = Math.min(rawRows, 8);
  const pad = 12;
  const cellW = (size - pad * 2) / cols;
  const cellH = (size * 0.88 - pad * 2) / rows;
  const cells = [];
  for (let r = 0; r < rows; r++) {
    for (let col = 0; col < cols; col++) {
      const n = startAt + r * cols + col;
      const x = pad + col * cellW, y = pad + r * cellH;
      const isHi = highlight.includes(n);
      const isCross = crossed.includes(n);
      // Cell body
      if (isHi) {
        cells.push(
          <g key={`bg${r}${col}`} filter={`url(#${uid}_rich)`}>
            <rect x={x + 1} y={y + 1} width={cellW - 2} height={cellH - 2}
              fill={c.fill1} rx={4} />
            <rect x={x + 1} y={y + 1} width={cellW - 2} height={cellH - 2}
              fill={`url(#${uid}_hi)`} rx={4} />
            {/* Top specular stripe */}
            <rect x={x + 3} y={y + 2} width={cellW - 6} height={cellH * 0.25}
              fill="#fff" opacity="0.30" rx={2} />
          </g>
        );
      } else {
        cells.push(
          <rect key={`bg${r}${col}`} x={x + 0.5} y={y + 0.5}
            width={cellW - 1} height={cellH - 1}
            fill={`url(#${uid}_cell)`} stroke={c.line} strokeWidth={0.6}
            strokeOpacity="0.55" rx={3} />
        );
      }
      cells.push(
        <text key={`t${r}${col}`} x={x + cellW / 2} y={y + cellH / 2 + 4}
          textAnchor="middle" fontSize={Math.min(cellW * 0.45, 12)}
          fontWeight={isHi ? 800 : 600}
          fill={isHi ? '#fff' : isCross ? c.muted : c.text}
          opacity={isCross ? 0.55 : 1}
          style={isHi ? { filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.25))' } : undefined}>
          {n}
        </text>
      );
      if (isCross) {
        cells.push(
          <line key={`x1${r}${col}`} x1={x + 5} y1={y + 5} x2={x + cellW - 5} y2={y + cellH - 5}
            stroke={c.fill5} strokeWidth={1.7} strokeLinecap="round" opacity="0.80" />,
          <line key={`x2${r}${col}`} x1={x + cellW - 5} y1={y + 5} x2={x + 5} y2={y + cellH - 5}
            stroke={c.fill5} strokeWidth={1.7} strokeLinecap="round" opacity="0.80" />
        );
      }
    }
  }
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="jimarên risteyê">
      <defs>
        <RichShadow id={`${uid}_rich`} near={0.8} far={1.8} opacityNear={0.20} opacityFar={0.08} />
        <linearGradient id={`${uid}_cell`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor={lighten(c.bg, 0.05)} />
          <stop offset="100%" stopColor={c.bg} />
        </linearGradient>
        <linearGradient id={`${uid}_hi`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor={lighten(c.fill1, 0.20)} />
          <stop offset="100%" stopColor={c.fill1} />
        </linearGradient>
      </defs>
      {cells}
    </svg>
  );
}

// ─── 23. ArrowSequenceVisual (NEW) ───────────────────────────────────────────
// Polished: input/output mapping table with glossy spherical bubbles. Logo
// teal for input, coral for output. The operation label sits in a central
// pill that visually "transforms" each input into its paired output.
export function ArrowSequenceVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const { from = [1, 2, 3], to = [2, 4, 6], label = '×2' } = params;
  const n = Math.min(from.length, to.length, 4);
  const rowH = (size * 0.78) / (n + 0.5);
  const lx = size * 0.18, rx = size * 0.82;
  const midX = size * 0.5;
  const topY = size * 0.15;
  const bubbleR = 17;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="rêzik">
      <defs>
        <RichShadow id={`${uid}_rich`} near={1} far={2.5} />
        <SphereGradient id={`${uid}_inG`}  base={c.fill1} cx="32%" cy="28%" />
        <SphereGradient id={`${uid}_outG`} base={c.fill2} cx="32%" cy="28%" />
        <linearGradient id={`${uid}_arrow`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor={c.fill1} />
          <stop offset="100%" stopColor={c.fill2} />
        </linearGradient>
        <marker id={`${uid}_aseq`} markerWidth="6.5" markerHeight="6.5" refX="3.5" refY="3.25" orient="auto">
          <path d="M0,0.5 L6,3.25 L0,6 Z" fill={c.fill2} />
        </marker>
      </defs>

      {/* Column headers (chips) */}
      <g transform={`translate(${lx}, ${topY - 4})`}>
        <rect x={-22} y={-9} width={44} height={16} rx={8} fill={c.fill1} opacity="0.12" />
        <text x={0} y={1} textAnchor="middle" dominantBaseline="middle"
          fontSize={9} fontWeight={800} fill={c.fill1}>Têkeve</text>
      </g>
      <g transform={`translate(${rx}, ${topY - 4})`}>
        <rect x={-20} y={-9} width={40} height={16} rx={8} fill={c.fill2} opacity="0.12" />
        <text x={0} y={1} textAnchor="middle" dominantBaseline="middle"
          fontSize={9} fontWeight={800} fill={c.fill2}>Derxe</text>
      </g>

      {/* Operation label — central pill with gradient */}
      <g transform={`translate(${midX}, ${topY + (n / 2) * rowH})`}>
        <rect x={-22} y={-13} width={44} height={26} rx={13}
          fill={`url(#${uid}_arrow)`} opacity="0.22" />
        <rect x={-22} y={-13} width={44} height={26} rx={13}
          fill="none" stroke={c.fill2} strokeWidth={1.2} strokeOpacity="0.55" />
        <text x={0} y={1} textAnchor="middle" dominantBaseline="middle"
          fontSize={13} fontWeight={900} fill={c.fill2}>{label}</text>
      </g>

      {Array.from({ length: n }, (_, i) => {
        const y = topY + (i + 0.7) * rowH;
        return (
          <g key={i}>
            {/* From bubble — glossy sphere */}
            <g filter={`url(#${uid}_rich)`}>
              <circle cx={lx} cy={y} r={bubbleR} fill={c.fill1} />
              <circle cx={lx} cy={y} r={bubbleR} fill={`url(#${uid}_inG)`} />
            </g>
            <ellipse cx={lx - bubbleR * 0.32} cy={y - bubbleR * 0.40}
              rx={bubbleR * 0.30} ry={bubbleR * 0.18}
              fill="#fff" opacity="0.55" />
            <text x={lx} y={y + 4} textAnchor="middle" fontSize={12} fontWeight={800} fill="#fff"
              style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.25))' }}>
              {from[i]}
            </text>

            {/* Arrow — gradient stroke */}
            <line x1={lx + bubbleR + 1} y1={y} x2={rx - bubbleR - 1} y2={y}
              stroke={`url(#${uid}_arrow)`} strokeWidth={2.2}
              strokeLinecap="round" markerEnd={`url(#${uid}_aseq)`} />

            {/* To bubble */}
            <g filter={`url(#${uid}_rich)`}>
              <circle cx={rx} cy={y} r={bubbleR} fill={c.fill2} />
              <circle cx={rx} cy={y} r={bubbleR} fill={`url(#${uid}_outG)`} />
            </g>
            <ellipse cx={rx - bubbleR * 0.32} cy={y - bubbleR * 0.40}
              rx={bubbleR * 0.30} ry={bubbleR * 0.18}
              fill="#fff" opacity="0.55" />
            <text x={rx} y={y + 4} textAnchor="middle" fontSize={12} fontWeight={800} fill="#fff"
              style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.25))' }}>
              {to[i]}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ─── 24. AreaModelVisual (NEW) ───────────────────────────────────────────────
// Polished: a × b rectangle modelled as a checkerboard of softly tinted unit
// cells (each cell carries a subtle vertical gradient so the grid feels like
// a tiled mosaic rather than a flat outline). Dimension labels live in pill
// badges along the outside edges.
export function AreaModelVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const { a = 4, b = 3 } = params;
  const pad = 30;
  const aW = size - pad * 2, aH = size * 0.60 - pad * 0.4;
  const cellW = aW / a, cellH = aH / b;
  const cells = [];
  for (let row = 0; row < b; row++) {
    for (let col = 0; col < a; col++) {
      const isAlt = (row + col) % 2 === 1;
      cells.push(
        <rect key={`${row}${col}`}
          x={pad + col * cellW + 1.5} y={pad + row * cellH + 1.5}
          width={cellW - 3} height={cellH - 3}
          fill={isAlt ? `url(#${uid}_cellB)` : `url(#${uid}_cellA)`}
          stroke={`${c.fill1}40`} strokeWidth={0.6} rx={3} />
      );
    }
  }
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
      aria-label={`${a} × ${b} = ${a * b}`}>
      <defs>
        <RichShadow id={`${uid}_rich`} near={1.2} far={3} />
        <linearGradient id={`${uid}_cellA`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor={lighten(c.fill1, 0.85)} />
          <stop offset="100%" stopColor={c.soft1} />
        </linearGradient>
        <linearGradient id={`${uid}_cellB`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor={c.soft1} />
          <stop offset="100%" stopColor={lighten(c.fill1, 0.45)} />
        </linearGradient>
      </defs>

      {/* Outer rectangle with shadow */}
      <g filter={`url(#${uid}_rich)`}>
        <rect x={pad} y={pad} width={aW} height={aH}
          fill="none" stroke={c.fill1} strokeWidth={2.5}
          rx={5} />
      </g>
      {cells}

      {/* Width label pill */}
      <g transform={`translate(${pad + aW / 2}, ${pad - 12})`}>
        <rect x={-14} y={-9} width={28} height={18} rx={9}
          fill={c.fill1} opacity="0.16" />
        <text x={0} y={1} textAnchor="middle" dominantBaseline="middle"
          fontSize={11} fontWeight={800} fill={c.fill1}>{a}</text>
      </g>
      {/* Height label pill (rotated) */}
      <g transform={`translate(${pad - 12}, ${pad + aH / 2}) rotate(-90)`}>
        <rect x={-14} y={-9} width={28} height={18} rx={9}
          fill={c.fill1} opacity="0.16" />
        <text x={0} y={1} textAnchor="middle" dominantBaseline="middle"
          fontSize={11} fontWeight={800} fill={c.fill1}>{b}</text>
      </g>
      {/* Result — clamped to stay inside viewBox */}
      <text x={size/2} y={Math.min(pad + aH + 24, size - 6)} textAnchor="middle"
        fontSize={12} fontWeight={800} fill={c.fill2}>{a} × {b} = {a * b}</text>
    </svg>
  );
}

// ─── 25a. RegroupingVisual (eldeli toplama / onluk bozma) ───────────────────
export function RegroupingVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const { op = 'add', a = 18, b = 24, carry = true } = params;
  const result = op === 'add' ? a + b : a - b;
  const opSym = op === 'add' ? '+' : '−';
  const opColor = op === 'add' ? c.fill3 : c.fill5;
  // Carry/borrow indicator uses logo palette (coral for add, dark teal for sub)
  const carryColor = op === 'add' ? c.fill2 : c.fill1;

  const cx = size / 2;
  const startY = size * 0.15;
  const rowH = size * 0.18;
  const digitW = size * 0.14;
  const fs = size * 0.12;
  const fsSmall = size * 0.07;

  const aStr = String(a).padStart(2, ' ');
  const bStr = String(b).padStart(2, ' ');
  const maxLen = Math.max(aStr.length, bStr.length, String(result).length);
  const rPad = String(result).padStart(maxLen, ' ');
  const rightX = cx + digitW * 0.9;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
      aria-label={`${a} ${opSym} ${b} = ${result}`}>
      <defs>
        <RichShadow id={`${uid}_rich`} near={1.5} far={3.5} />
        <linearGradient id={`${uid}_paper`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor={lighten(c.bg, 0.05)} />
          <stop offset="100%" stopColor={c.bg} />
        </linearGradient>
        {carry && (
          <marker id={`${uid}_arr`} markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
            <path d="M0,1 L5,3 L0,5 Z" fill={carryColor} />
          </marker>
        )}
      </defs>

      {/* Notebook card background — gradient + soft shadow */}
      <g filter={`url(#${uid}_rich)`}>
        <rect x={size*0.10} y={size*0.06} width={size*0.80} height={size*0.74}
          rx={size*0.06} fill={`url(#${uid}_paper)`} stroke={c.line} strokeWidth={0.6}
          strokeOpacity={0.5} />
      </g>
      {/* Subtle inner top highlight for the paper feel */}
      <rect x={size*0.11} y={size*0.07} width={size*0.78} height={size*0.04}
        rx={size*0.04} fill="#fff" opacity="0.40" />

      {/* Carry indicator */}
      {carry && op === 'add' && (
        <>
          <path d={`M${rightX} ${startY + rowH*0.3} Q${rightX - digitW*0.5} ${startY - rowH*0.1} ${rightX - digitW} ${startY + rowH*0.3}`}
            fill="none" stroke={carryColor} strokeWidth={1.5} strokeDasharray="3 2"
            markerEnd={`url(#${uid}_arr)`} />
          <circle cx={rightX - digitW} cy={startY + rowH*0.22} r={fsSmall * 0.85}
            fill={carryColor} opacity="0.15" />
          <text x={rightX - digitW} y={startY + rowH*0.22}
            textAnchor="middle" dominantBaseline="middle"
            fontSize={fsSmall} fontWeight={800} fill={carryColor}>1</text>
        </>
      )}
      {carry && op === 'sub' && (
        <>
          <path d={`M${rightX - digitW} ${startY + rowH*0.3} Q${rightX - digitW*0.5} ${startY - rowH*0.1} ${rightX} ${startY + rowH*0.3}`}
            fill="none" stroke={carryColor} strokeWidth={1.5} strokeDasharray="3 2"
            markerEnd={`url(#${uid}_arr)`} />
          <line x1={rightX - digitW - digitW*0.15} y1={startY + rowH*0.5}
            x2={rightX - digitW + digitW*0.15} y2={startY + rowH*0.8}
            stroke={carryColor} strokeWidth={1.4} strokeLinecap="round" />
        </>
      )}

      {/* Row 1: first number */}
      {aStr.split('').map((d, i) => (
        <text key={`a${i}`} x={rightX - (aStr.length - 1 - i) * digitW} y={startY + rowH}
          textAnchor="middle" fontSize={fs} fontWeight={700} fill={c.text}
          fontFamily="'Inter',system-ui,monospace">{d}</text>
      ))}

      {/* Row 2: operator + second number */}
      <text x={rightX - maxLen * digitW + digitW*0.15} y={startY + rowH*2}
        textAnchor="middle" fontSize={fs} fontWeight={800} fill={opColor}
        fontFamily="'Inter',system-ui,monospace">{opSym}</text>
      {bStr.split('').map((d, i) => (
        <text key={`b${i}`} x={rightX - (bStr.length - 1 - i) * digitW} y={startY + rowH*2}
          textAnchor="middle" fontSize={fs} fontWeight={700} fill={c.text}
          fontFamily="'Inter',system-ui,monospace">{d}</text>
      ))}

      {/* Line */}
      <line x1={size*0.16} y1={startY + rowH*2.28} x2={size*0.84} y2={startY + rowH*2.28}
        stroke={c.text} strokeWidth={1.8} />

      {/* Row 3: result */}
      {rPad.split('').map((d, i) => (
        <text key={`r${i}`} x={rightX - (rPad.length - 1 - i) * digitW} y={startY + rowH*3.15}
          textAnchor="middle" fontSize={fs} fontWeight={800} fill={opColor}
          fontFamily="'Inter',system-ui,monospace">{d}</text>
      ))}

      {/* Label */}
      <text x={cx} y={size * 0.92} textAnchor="middle"
        fontSize={fsSmall} fontWeight={600} fill={c.dim}>
        {carry ? (op === 'add' ? 'didestdemayî' : 'bi hûrkirinê') : (op === 'add' ? 'bêdestdemayî' : 'bê hûrkirin')}
      </text>
    </svg>
  );
}

// ─── 25b. CustomVisual ──────────────────────────────────────────────────────
// Polished: large emoji centred over a subtle teal soft-tint disc; formula
// and label appear in tinted pill badges, matching the rest of the library.
export function CustomVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  // Thumbnails show the emoji only: 8–13px labels are clipped at list size.
  const isCompact = size < 64;
  const { icon = '🔢' } = params;
  const label = isCompact ? '' : (params.label || '');
  const formula = isCompact ? '' : (params.formula || '');
  const formulaFs = formula.length > 14 ? 9 : formula.length > 10 ? 11 : 13;
  const labelFs   = label.length > 16 ? 8 : 10;
  const hasFormulaOrLabel = formula || label;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={params.label || icon}>
      <defs>
        <radialGradient id={`${uid}_disc`} cx="50%" cy="50%" r="55%">
          <stop offset="0%"   stopColor={c.fill1} stopOpacity="0.14" />
          <stop offset="100%" stopColor={c.fill1} stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* Soft glow disc behind the emoji */}
      <circle cx={size / 2} cy={size * (hasFormulaOrLabel ? 0.40 : 0.50)} r={size * 0.32}
        fill={`url(#${uid}_disc)`} />
      <text x={size / 2} y={size * (hasFormulaOrLabel ? 0.40 : 0.50)} textAnchor="middle"
        fontSize={size * (isCompact ? 0.5 : 0.32)} dominantBaseline="middle">{icon}</text>
      {formula && (
        <text x={size / 2} y={size * 0.68} textAnchor="middle"
          fontSize={formulaFs} fontWeight={800} fill={c.fill1} fontFamily="'Inter',system-ui,monospace">
          {formula.length > 18 ? formula.slice(0, 17) + '…' : formula}
        </text>
      )}
      {label && (
        <text x={size/2} y={size * (formula ? 0.84 : 0.72)} textAnchor="middle"
          fontSize={labelFs} fontWeight={600} fill={c.dim}>
          {label.length > 20 ? label.slice(0, 19) + '…' : label}
        </text>
      )}
    </svg>
  );
}

// ─── 27. SpatialVisual ───────────────────────────────────────────────────────
// Position words shown the way they are taught in preschool: a small scene
// with a reference object and counters placed relative to it, each labelled
// with the word it stands for (labels come from the dictionary's own terms).
// `showLabels: false` drops the words, e.g. for question cards.
function ScenePill({ x, y, text, color, unit }) {
  const fontSize = 12.5 * unit;
  const width = text.length * fontSize * 0.62 + 16 * unit;
  const height = 19 * unit;
  return (
    <g transform={`translate(${x}, ${y})`}>
      <rect x={-width / 2} y={-height / 2} width={width} height={height} rx={height / 2}
        fill={color} opacity="0.15" />
      <text x={0} y={1} textAnchor="middle" dominantBaseline="middle"
        fontSize={fontSize} fontWeight={800} fill={color}>{text}</text>
    </g>
  );
}

function SceneArrow({ x1, y1, x2, y2, color, width }) {
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const head = width * 3.4;
  const bx = x2 - Math.cos(angle) * head, by = y2 - Math.sin(angle) * head;
  const nx = Math.sin(angle) * head * 0.62, ny = -Math.cos(angle) * head * 0.62;
  return (
    <g>
      <line x1={x1} y1={y1} x2={bx} y2={by} stroke={color} strokeWidth={width} strokeLinecap="round" />
      <polygon points={`${x2},${y2} ${bx + nx},${by + ny} ${bx - nx},${by - ny}`} fill={color} />
    </g>
  );
}

export function SpatialVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const { scene = 'above_below', showLabels = true } = params;
  const u = size / 160;
  const at = f => f * size;
  const r = at(0.09);
  // Reference objects (table, box, house) share one warm neutral.
  const wood = lighten(c.fill4, 0.15), woodEdge = darken(c.fill4, 0.30);

  const ball = (key, fx, fy, tone, radius = r) => (
    <Sphere key={key} x={at(fx)} y={at(fy)} r={radius}
      base={tone === 2 ? c.fill2 : c.fill1} gradientId={`${uid}_g${tone}`} filterId={`${uid}_rich`} />
  );
  const label = (key, fx, fy, text, tone) => (showLabels
    ? <ScenePill key={key} x={at(fx)} y={at(fy)} text={text} color={tone === 2 ? c.fill2 : c.fill1} unit={u} />
    : null);
  const block = (key, fx, fy, fw, fh) => (
    <rect key={key} x={at(fx)} y={at(fy)} width={at(fw)} height={at(fh)} rx={4 * u}
      fill={wood} stroke={woodEdge} strokeWidth={1.6 * u} filter={`url(#${uid}_rich)`} />
  );
  const ground = (key, fy, fx1 = 0.08, fx2 = 0.92) => (
    <line key={key} x1={at(fx1)} y1={at(fy)} x2={at(fx2)} y2={at(fy)}
      stroke={c.muted} strokeWidth={1.6 * u} strokeLinecap="round" opacity="0.55" />
  );

  let content;
  let ariaLabel;
  switch (scene) {
    case 'up':
    case 'down': {
      const isUp = scene === 'up';
      const tone = isUp ? 1 : 2;
      ariaLabel = isUp ? 'jor' : 'jêr';
      content = [
        ground('g', 0.86),
        <SceneArrow key="a" x1={at(0.5)} y1={at(isUp ? 0.74 : 0.30)} x2={at(0.5)} y2={at(isUp ? 0.26 : 0.70)}
          color={tone === 2 ? c.fill2 : c.fill1} width={7 * u} />,
        ball('b', 0.5, isUp ? 0.77 : 0.23, tone),
        label('l', 0.5, isUp ? 0.13 : 0.93, isUp ? 'Jor' : 'Jêr', tone),
      ];
      break;
    }
    case 'left_right':
      ariaLabel = 'rast û çep';
      content = [
        ground('g', 0.74),
        block('o', 0.43, 0.40, 0.14, 0.34),
        ball('l', 0.20, 0.65, 2), ball('r', 0.80, 0.65, 1),
        label('ll', 0.20, 0.42, 'Çep', 2), label('lr', 0.80, 0.42, 'Rast', 1),
        <SceneArrow key="al" x1={at(0.36)} y1={at(0.88)} x2={at(0.10)} y2={at(0.88)} color={c.fill2} width={4 * u} />,
        <SceneArrow key="ar" x1={at(0.64)} y1={at(0.88)} x2={at(0.90)} y2={at(0.88)} color={c.fill1} width={4 * u} />,
      ];
      break;
    case 'front_back':
      ariaLabel = 'pêş û paş';
      content = [
        <rect key="back" x={at(0.42)} y={at(0.22)} width={at(0.38)} height={at(0.38)} rx={6 * u}
          fill={c.soft1} stroke={c.fill1} strokeWidth={2 * u} filter={`url(#${uid}_rich)`} />,
        <rect key="front" x={at(0.20)} y={at(0.42)} width={at(0.38)} height={at(0.38)} rx={6 * u}
          fill={c.fill2} stroke={darken(c.fill2, 0.15)} strokeWidth={2 * u} filter={`url(#${uid}_rich)`} />,
        label('lb', 0.74, 0.12, 'Paş', 1), label('lf', 0.39, 0.91, 'Pêş', 2),
      ];
      break;
    case 'inside_outside':
      ariaLabel = 'hundur û derve';
      content = [
        ground('g', 0.82),
        <path key="box" d={`M${at(0.16)},${at(0.42)} V${at(0.80)} H${at(0.58)} V${at(0.42)}`}
          fill={wood} fillOpacity="0.35" stroke={woodEdge} strokeWidth={4 * u}
          strokeLinecap="round" strokeLinejoin="round" />,
        ball('in', 0.37, 0.69, 1), ball('out', 0.80, 0.72, 2),
        label('li', 0.37, 0.30, 'Hundur', 1), label('lo', 0.80, 0.52, 'Derve', 2),
      ];
      break;
    case 'near_far':
      ariaLabel = 'nêzîk û dûr';
      content = [
        ground('g', 0.80),
        // House: the thing the two counters are near to / far from
        <polygon key="roof" points={`${at(0.07)},${at(0.56)} ${at(0.19)},${at(0.42)} ${at(0.31)},${at(0.56)}`}
          fill={c.fill2} stroke={darken(c.fill2, 0.15)} strokeWidth={1.4 * u} strokeLinejoin="round" />,
        block('house', 0.10, 0.56, 0.18, 0.24),
        ball('near', 0.46, 0.71, 1), ball('far', 0.86, 0.73, 2, r * 0.72),
        label('ln', 0.46, 0.51, 'Nêzîk', 1), label('lf', 0.84, 0.56, 'Dûr', 2),
      ];
      break;
    case 'between':
      ariaLabel = 'navbêr';
      content = [
        ground('g', 0.74),
        block('a', 0.10, 0.44, 0.20, 0.30), block('b', 0.70, 0.44, 0.20, 0.30),
        ball('m', 0.5, 0.65, 2),
        label('l', 0.5, 0.40, 'Navbêr', 2),
      ];
      break;
    case 'forward':
    case 'backward': {
      const isForward = scene === 'forward';
      const tone = isForward ? 1 : 2;
      ariaLabel = isForward ? 'pêşve' : 'paşve';
      content = [
        ground('g', 0.72),
        ball('b', isForward ? 0.22 : 0.78, 0.63, tone),
        <SceneArrow key="a" x1={at(isForward ? 0.38 : 0.62)} y1={at(0.63)} x2={at(isForward ? 0.88 : 0.12)} y2={at(0.63)}
          color={tone === 2 ? c.fill2 : c.fill1} width={6 * u} />,
        label('l', isForward ? 0.62 : 0.38, 0.42, isForward ? 'Pêşve' : 'Paşve', tone),
      ];
      break;
    }
    default: // above_below
      ariaLabel = 'ser û bin';
      content = [
        ground('g', 0.86),
        // Table: slab + two legs
        block('leg1', 0.28, 0.54, 0.045, 0.32), block('leg2', 0.675, 0.54, 0.045, 0.32),
        block('top', 0.22, 0.48, 0.56, 0.07),
        ball('on', 0.5, 0.39, 1), ball('under', 0.5, 0.77, 2),
        label('lo', 0.5, 0.18, 'Ser', 1), label('lu', 0.86, 0.70, 'Bin', 2),
      ];
  }

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={ariaLabel}>
      <defs>
        <RichShadow id={`${uid}_rich`} near={0.8} far={2.2} opacityNear={0.18} opacityFar={0.10} />
        <SphereGradient id={`${uid}_g1`} base={c.fill1} cx="32%" cy="28%" />
        <SphereGradient id={`${uid}_g2`} base={c.fill2} cx="32%" cy="28%" />
      </defs>
      {content}
    </svg>
  );
}

// ─── 28. OrdinalVisual ───────────────────────────────────────────────────────
// A queue of counters numbered in order; `highlight` (1-based) picks out one
// position so "first / second / third" is about place, not quantity.
export function OrdinalVisual({ params = {}, theme, size = 160 }) {
  const uid = useId().replace(/:/g, '');
  const c = vColors(theme);
  const count = Math.max(2, Math.min(params.count ?? 5, 7));
  const highlight = params.highlight ?? null;
  const u = size / 160;
  const cell = (size * 0.86) / count;
  const r = Math.min(cell * 0.38, 15 * u);
  const startX = size * 0.07 + cell / 2;
  const rowY = size * 0.56;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
      aria-label={highlight ? `rêza ${highlight}` : 'hejmarên rêzebûnê'}>
      <defs>
        <RichShadow id={`${uid}_rich`} near={0.8} far={2.2} opacityNear={0.18} opacityFar={0.10} />
        <SphereGradient id={`${uid}_g1`} base={c.fill1} cx="32%" cy="28%" />
        <SphereGradient id={`${uid}_g2`} base={c.fill2} cx="32%" cy="28%" />
      </defs>
      {Array.from({ length: count }, (_, i) => {
        const x = startX + i * cell;
        const isHi = highlight === i + 1;
        const isDim = highlight !== null && !isHi;
        const color = isHi ? c.fill2 : c.fill1;
        return (
          <g key={i}>
            <Sphere x={x} y={rowY} r={isHi ? r * 1.18 : r} base={color}
              gradientId={`${uid}_g${isHi ? 2 : 1}`} filterId={`${uid}_rich`} opacity={isDim ? 0.45 : 1} />
            {isHi && (
              <circle cx={x} cy={rowY - r * 1.18 - 16 * u} r={12 * u} fill={c.fill2} opacity="0.16" />
            )}
            <text x={x} y={rowY - (isHi ? r * 1.18 + 16 * u : r + 11 * u) + 1} textAnchor="middle" dominantBaseline="middle"
              fontSize={(isHi ? 14 : 10.5) * u} fontWeight={isHi ? 900 : 700}
              fill={isHi ? c.fill2 : c.dim} opacity={isDim ? 0.7 : 1}>
              {i + 1}.
            </text>
          </g>
        );
      })}
      {/* Counting direction */}
      <SceneArrow x1={size * 0.12} y1={size * 0.84} x2={size * 0.88} y2={size * 0.84}
        color={c.muted} width={2.4 * u} />
    </svg>
  );
}

// ─── 26. ConceptVisual (router) ──────────────────────────────────────────────
// Drawn visuals lay themselves out in absolute units and break down (negative
// rect sizes, clipped labels) below roughly this size, so thumbnails are
// rendered at THUMB_RENDER_SIZE and scaled down as a faithful miniature.
const THUMB_RENDER_SIZE = 96;

export function ConceptVisual({ visual, theme, size = 160 }) {
  if (!visual || visual.type === 'none') return null;
  const c = vColors(theme);
  const t = theme;
  const p = visual.params || {};
  const frame = size;
  const isThumb = frame < THUMB_RENDER_SIZE;

  const renderInner = (rs) => {
    switch (visual.type) {
      case 'number_line':    return <NumberLineVisual params={p} theme={t} size={rs} />;
      case 'counting':       return <CountingVisual params={p} theme={t} size={rs} />;
      case 'blocks':         return <BlocksVisual params={p} theme={t} size={rs} />;
      case 'fraction':       return <FractionVisual params={p} theme={t} size={rs} />;
      case 'operation':      return <OperationVisual params={p} theme={t} size={rs} />;
      case 'compare':        return <CompareVisual params={p} theme={t} size={rs} />;
      case 'point':          return <GeometryVisual visual={visual} params={{...p, type:'point'}} theme={t} size={rs} />;
      case 'line':           return <GeometryVisual visual={visual} params={{...p, type: p.type || 'line'}} theme={t} size={rs} />;
      case 'triangle':       return <GeometryVisual visual={visual} params={{...p, type: p.type || 'triangle'}} theme={t} size={rs} />;
      case 'quadrilateral':  return <GeometryVisual visual={visual} params={{...p, type: p.type || 'quadrilateral'}} theme={t} size={rs} />;
      case 'circle':         return <GeometryVisual visual={visual} params={{...p, type:'circle'}} theme={t} size={rs} />;
      case 'angle':          return <GeometryVisual visual={visual} params={{...p, type:'angle'}} theme={t} size={rs} />;
      case 'geometry_3d':    return <Geometry3DVisual params={p} theme={t} size={rs} />;
      case 'coordinate':     return <CoordinateVisual params={p} theme={t} size={rs} />;
      case 'symmetry':       return <SymmetryVisual params={p} theme={t} size={rs} />;
      case 'ruler':          return <RulerVisual params={p} theme={t} size={rs} />;
      case 'scale':          return <ScaleVisual params={p} theme={t} size={rs} />;
      case 'clock':          return <ClockVisual params={p} theme={t} size={rs} />;
      case 'bar_chart':      return <BarChartVisual params={p} theme={t} size={rs} />;
      case 'pie_chart':      return <PieChartVisual params={p} theme={t} size={rs} />;
      case 'table':          return <TableVisual params={p} theme={t} size={rs} />;
      case 'dice':           return <DiceVisual params={p} theme={t} size={rs} />;
      case 'probability':    return <ProbabilityVisual params={p} theme={t} size={rs} />;
      case 'variable':       return <AlgebraVisual params={{...p, type:'variable'}} theme={t} size={rs} />;
      case 'equation':       return <AlgebraVisual params={{...p, type:'equation'}} theme={t} size={rs} />;
      case 'pattern':        return <PatternVisual params={p} theme={t} size={rs} />;
      case 'set':            return <SetVisual params={p} theme={t} size={rs} />;
      case 'number_grid':    return <NumberGridVisual params={p} theme={t} size={rs} />;
      case 'arrow_sequence': return <ArrowSequenceVisual params={p} theme={t} size={rs} />;
      case 'area_model':     return <AreaModelVisual params={p} theme={t} size={rs} />;
      case 'regrouping':     return <RegroupingVisual params={p} theme={t} size={rs} />;
      case 'spatial':        return <SpatialVisual params={p} theme={t} size={rs} />;
      case 'ordinal':        return <OrdinalVisual params={p} theme={t} size={rs} />;
      default:               return <CustomVisual params={p} theme={t} size={frame} />;
    }
  };

  const inner = renderInner(isThumb ? THUMB_RENDER_SIZE : frame);
  const isScaled = isThumb && inner.type !== CustomVisual;
  return (
    <div style={{
      width: frame, height: frame, flexShrink: 0,
      borderRadius: isThumb ? 8 : 12,
      background: c.soft1,
      border: '1px solid ' + c.line,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden',
    }}>
      {isScaled ? (
        <div style={{
          width: THUMB_RENDER_SIZE, height: THUMB_RENDER_SIZE, flexShrink: 0,
          transform: `scale(${frame / THUMB_RENDER_SIZE})`,
        }}>
          {inner}
        </div>
      ) : inner}
    </div>
  );
}
